import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js'
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

import { startKeyboardTargetLifecycle } from '../animationWindow'
import { isKeyboardEventLike, isTextEntryTarget } from '../eventGuards'
import { createMaterialTexture } from './materialTextures'
import { MOON_LIGHT_DIRECTION, createNightSky } from './nightSky'
import { createStaticMeshView } from './staticMeshView'
import { createTerrainView } from './terrainView'
import { loadRobloxMeshGeometry } from './loadRobloxMesh'
import { createSharedTextureCache } from './meshTextureCache'
import {
  BatchableObject,
  StaticBoxBatcher,
  createStaticBoxBatcher,
  isBatchableDescriptor,
} from './staticBoxBatcher'

import {
  EMISSIVE_COLOR_MATERIALS,
  AVATAR_ID,
  AvatarMotionState,
  ObjectRuntimeState,
  PreviewWorld,
  RobloxMaterial,
  SceneDescriptor,
  SceneObjectDescriptor,
  ShapeKind,
  Vec3,
} from '../types'

import { VRMLoaderPlugin, VRMUtils } from '@pixiv/three-vrm'

import {
  MATERIAL_SPECS,
  applyInstancedPatternShader,
  applyInstancedStudShader,
  createStudTexture,
} from './materialSpecs'
import {
  MeshEntry,
  createMeshEntry,
  cylinderAxisOf,
  disposeMeshEntry,
  disposeModel,
  toVector3,
} from './meshEntry'
import {
  bleedCutoutTexture,
  loadDecalsFor,
  loadSurfaceTextFor,
} from './decalLoader'
import { applyState, collectModelMaterials } from './applyObjectState'
import { isClickableState } from './clickableState'
import {
  FIST_BONE_PAIRS,
  FIST_THUMB_BEND_Z,
  FIST_THUMB_CURL_Y,
  FIST_THUMB_PAIRS,
  HAKASE_VRM_URL,
  VrmAvatarEntry,
  applyRestPoseAdjustments,
  applyVrmAvatarState,
  createVrmAvatarEntry,
  disposeVrmAvatarEntry,
  extractVrm,
  layoutVrmAvatar,
} from './avatar/vrmAvatarEntry'
import { animateVrmAvatar } from './avatar/animateVrmAvatar'
import {
  CameraMode,
  GizmoMode,
  PreviewRendererHandle,
  PreviewRendererOptions,
} from './rendererTypes'

export type {
  CameraMode,
  GizmoMode,
  ObjectTransform,
  PreviewRendererHandle,
  PreviewRendererOptions,
} from './rendererTypes'

/** Roblox 風スカイの鮮やかな水色背景。 */
const BACKGROUND_COLOR = 0x80c1ff

/** 夜空の背景色（夜帯シーンのフォールバック背景。実体は夜空ドームが覆う）。 */
const NIGHT_BACKGROUND_COLOR = 0x05081c

/**
 * シーンが夜（Roblox の Lighting.TimeOfDay が夜帯）かどうか。
 * Roblox 慣習で 18 時〜翌 6 時を夜とみなす（RJF08=19時、RJF11=0時が該当）。
 */
const isNightScene = (descriptor: SceneDescriptor): boolean => {
  const clockTime = descriptor.lighting?.clockTime
  if (clockTime === undefined) {
    return false
  }
  return clockTime >= 18 || clockTime < 6
}
/** カメラ自動フィット時の視野角（度）。 */
const CAMERA_FOV = 50
/** バウンディング球半径に対するカメラ距離の倍率。 */
const CAMERA_DISTANCE_FACTOR = 1.6
/** クリック判定とドラッグ操作を区別する移動量の閾値（px）。 */
const CLICK_DRAG_THRESHOLD = 4

/**
 * ホバー時のクリック可能判定 raycast の最小間隔（ms）。毎 pointermove で
 * raycast すると巨大シーンで無駄に重いので、この間隔未満の連続移動は間引く。
 */
const HOVER_RAYCAST_THROTTLE_MS = 70

/**
 * follow モードのズーム倍率（既定値・最小・最大）。
 * 既定は遠目（4.0）にして、アバターの周囲とこれから進む先を見渡しやすくする
 * （1.0 だと近すぎて足元しか見えないというフィードバックによる調整）。
 * ホイールで 0.35〜12.0 の範囲を自由に調整できる。
 */
const FOLLOW_DISTANCE_DEFAULT = 4.0
const FOLLOW_DISTANCE_MIN = 0.35
const FOLLOW_DISTANCE_MAX = 12.0
/**
 * free（固定）視点へ切り替えた直後の注視点からの距離倍率。
 * follow の基準距離（followDistance=1 相当）に対して掛け、すこし遠目に引いて
 * 全体を見渡しやすい初期視点にする。
 */
const FREE_ENTRY_DISTANCE_FACTOR = 5.0
/** wheel deltaY → followDistance のスケーリング係数（上スクロール=減算）。 */
const FOLLOW_WHEEL_STEP = 0.002

/** follow モードのマウスドラッグ感度（rad/px）。水平=ヨー、垂直=ピッチ。 */
const FOLLOW_DRAG_YAW_SENSITIVITY = 0.006
const FOLLOW_DRAG_PITCH_SENSITIVITY = 0.005
/**
 * 方向キー左右での視点回転の角速度（rad/秒）。Roblox 本家の既定操作
 * （←→ キーはキャラの横移動ではなくカメラの見回し）に合わせる。
 * 約 120°/秒 = 1 秒強で真後ろを向ける、ドラッグ操作と併用しても酔いにくい速さ。
 */
const ARROW_CAMERA_YAW_SPEED = (Math.PI * 2) / 3
/** ピッチの可動範囲（ラジアン、+ で上から見下ろし、- で下から見上げ）。 */
const FOLLOW_PITCH_MIN = -0.6
const FOLLOW_PITCH_MAX = 1.3
/** follow モードの既定ピッチ（上下バランス、約 26.6°）。実ピッチ = clamp(基準 + ユーザーオフセット)。 */
const FOLLOW_BASE_PITCH = Math.atan2(2.5, 5)

/**
 * 停止中の俯瞰ビュー（本家 Roblox の停止時に合わせ、上から見下ろす少し遠目の視点）。
 * 距離は既定 follow 距離の OVERVIEW_DISTANCE_FACTOR 倍、ピッチは約 54° の見下ろし。
 * ユーザーのホイールズーム値には依存させず、停止のたびに同じ見え方へ揃える。
 */
const OVERVIEW_DISTANCE_FACTOR = 1.6
const OVERVIEW_PITCH = 0.95

/** first モードの可変 FOV（最小・最大、度）。 */
const FIRST_FOV_MIN = 30
const FIRST_FOV_MAX = 90
/** wheel deltaY → FOV のスケーリング係数（度/deltaY、上スクロール=狭く=減算）。 */
const FIRST_FOV_WHEEL_STEP = 0.05

/**
 * follow モードのカメラ位置追従の平滑化係数（秒スケール）。
 * 各フレームで採用する補間率は `clamp(dt * SMOOTHING, 0, 1)`。dt=1/60 で POS=10 → 約 0.17。
 * 方位はプレイヤー操作（ドラッグ）主導でアバターを自動追従しないため平滑化しない。
 */
const FOLLOW_POS_SMOOTHING = 10
/**
 * 乗車中にカメラ方位を車の背後へ自動追従させる速度（1/s）。Roblox の
 * VehicleCamera は運転中にカメラが車の進行方向へゆっくり回り込む。
 * 歩行時に自動追従しないのは「カメラ基準で進む」移動との追いかけ合いを
 * 断つためだが、運転入力は車体基準（カメラ非依存）なので安全に回せる。
 */
const VEHICLE_CAMERA_YAW_FOLLOW = 2.5
/**
 * 乗車中のカメラ距離倍率。Roblox の VehicleCamera は被写体（車両）の
 * 大きさに合わせてズームアウトする。スポーツカーが画角へ収まる引きにする。
 */
const VEHICLE_CAMERA_DISTANCE_FACTOR = 2.0
/** 乗車ズーム（0..1）の追従速度（1/s）。乗降時の画角変化を滑らかにする。 */
const VEHICLE_CAMERA_ZOOM_LERP = 3

/** 視点とアバターの間の遮蔽物に与える不透明度（0=透明, 1=不透明）。 */
const OCCLUSION_OPACITY = 0.25
/** 遮蔽判定を行うフレーム間隔（毎フレームは重いので間引く）。 */
const OCCLUSION_CHECK_INTERVAL = 3
/** チャンク距離カリングを行うフレーム間隔（毎フレームは不要）。 */
const CULL_CHECK_INTERVAL = 10
/** アバター手前この距離（stud）以内の遮蔽は無視する（密着部品の誤フェード防止）。 */
const OCCLUSION_AVATAR_MARGIN = 1.5

/**
 * 全オブジェクトを内包するバウンディング球から、全体が収まるカメラ姿勢を算出する。
 * オブジェクトが空の場合は無難な既定値を返す。
 */
const computeAutoFit = (
  descriptor: SceneDescriptor,
): { position: THREE.Vector3; target: THREE.Vector3 } => {
  const box = new THREE.Box3()
  const expandBox = (center: Vec3, size: Vec3): void => {
    const c = toVector3(center)
    const half = new THREE.Vector3(size.x / 2, size.y / 2, size.z / 2)
    box.expandByPoint(c.clone().sub(half))
    box.expandByPoint(c.clone().add(half))
  }
  for (const obj of descriptor.objects) {
    expandBox(obj.position, obj.size)
  }
  // アバターがあれば spawn 位置も画角に含める（camera 指定時は未使用だが整合のため）。
  if (descriptor.avatar) {
    expandBox(descriptor.avatar.spawn, descriptor.avatar.size)
  }

  if (box.isEmpty()) {
    return {
      position: new THREE.Vector3(10, 10, 10),
      target: new THREE.Vector3(0, 0, 0),
    }
  }

  const sphere = box.getBoundingSphere(new THREE.Sphere())
  const target = sphere.center.clone()
  const fovRadians = (CAMERA_FOV * Math.PI) / 180
  const distance =
    (sphere.radius / Math.sin(fovRadians / 2)) * CAMERA_DISTANCE_FACTOR
  // 斜め上方からの俯瞰オフセット（正規化して距離を乗算）。
  const direction = new THREE.Vector3(1, 0.75, 1).normalize()
  const position = target.clone().add(direction.multiplyScalar(distance))
  return { position, target }
}

/**
 * `descriptor.objects` の id 重複を検証する。重複は orphan mesh / リークの
 * 原因になるため即エラー。WebGL/DOM などの副作用を起こす前に呼ぶことで、
 * throw 時のリークを防ぐ。
 */
const assertUniqueObjectIds = (descriptor: SceneDescriptor): void => {
  const seen = new Set<string>()
  for (const obj of descriptor.objects) {
    if (seen.has(obj.id)) {
      throw new Error(`Duplicate preview object id: ${obj.id}`)
    }
    seen.add(obj.id)
  }
}

/** descriptor.objects を id 索引化する（modelUrl 参照用）。 */
const indexDescriptors = (
  descriptor: SceneDescriptor,
): Map<string, SceneObjectDescriptor> => {
  const map = new Map<string, SceneObjectDescriptor>()
  for (const obj of descriptor.objects) {
    map.set(obj.id, obj)
  }
  return map
}

/**
 * シーン記述からメッシュ群を構築し、World 状態を購読して毎フレーム反映する
 * three.js 描画レイヤを生成する。
 */
export const createRenderer = (
  container: HTMLElement,
  descriptor: SceneDescriptor,
  world: PreviewWorld,
  options?: PreviewRendererOptions,
): PreviewRendererHandle => {
  // 副作用（WebGLRenderer 生成・canvas append）より前に記述を検証する。
  assertUniqueObjectIds(descriptor)
  const descriptorById = indexDescriptors(descriptor)
  const enableBuilder = options?.enableBuilder === true

  const initialWidth = Math.max(1, container.clientWidth)
  const initialHeight = Math.max(1, container.clientHeight)

  const scene = new THREE.Scene()
  // 夜帯シーン（RJF08=19時, RJF11=0時 等）は夜空ドーム + 月 + 星を背景に置く。
  const night = isNightScene(descriptor)
  scene.background = new THREE.Color(
    night ? NIGHT_BACKGROUND_COLOR : BACKGROUND_COLOR,
  )
  // 夜空はカメラに追従させて無限遠の背景として見せる（毎フレーム位置を同期）。
  const nightSky = night ? createNightSky() : null
  if (nightSky) {
    scene.add(nightSky.group)
  }

  // バッチ化した静的 box も遮蔽フェード（occlusion fade）対象。視点とアバターの間に
  // 入ったインスタンスは一時的に個別メッシュへ降格し（demoteBatchedForOcclusion）、
  // 個別メッシュと同じ滑らかな半透明（fadeEntry）でフェードする。遮蔽が解ければ
  // 再びバッチへ戻す。per-instance のシェーダ加工が要らず、見た目も個別と一致する。
  //
  // 巨大シーン（10k+ box）の描画コスト対策: 静的 box を「空間チャンク × Roblox
  // マテリアル」ごとの InstancedMesh へ集約し、ドローコールを激減させる。ビルダー
  // （編集）モードでは個別メッシュ + ギズモ操作が必要なため集約しない。
  const sharedStudTexture = createStudTexture()
  // バケットごとの模様テクスチャ（CanvasTexture）。material.dispose では
  // 解放されないため、ここで保持して disposeAll で明示的に解放する。
  const bucketPatternTextures: THREE.Texture[] = []
  const createBucketMaterial = (material: RobloxMaterial): THREE.Material => {
    const spec = MATERIAL_SPECS[material]
    const bucketMaterial = new THREE.MeshStandardMaterial({
      roughness: spec.roughness,
      metalness: spec.metalness,
    })
    // 固定色の弱発光（CrackedLava 等）はバケット単位で同一なのでここで反映する
    // （色追従の発光＝Neon/ForceField はバッチ対象外）。
    if (spec.emissiveHex !== undefined) {
      bucketMaterial.emissive.setHex(spec.emissiveHex)
      bucketMaterial.emissiveIntensity = 0.4
    }
    // 模様を持つマテリアル（Grass/Wood 等）は全面に模様をタイルし、個別メッシュ
    // の見た目（applyState の模様テクスチャ）に揃える。地形代替の芝生床などが
    // 無地+スタッドで Roblox らしく見えない問題への対応。無地マテリアルは従来
    // どおり上面スタッドのみ重ねる。
    const patternTexture = createMaterialTexture(material)
    if (patternTexture) {
      bucketPatternTextures.push(patternTexture)
      applyInstancedPatternShader(
        bucketMaterial,
        patternTexture,
        `instanced-pattern-${material}-v1`,
      )
    } else {
      // 上面（+Y 面）にだけスタッドを重ね、インスタンスの XZ スケールで studs を
      // タイルさせる（個別メッシュの per-size repeat 相当をシェーダで再現する）。
      applyInstancedStudShader(bucketMaterial, sharedStudTexture)
    }
    return bucketMaterial
  }
  const staticBoxBatcher: StaticBoxBatcher | null =
    enableBuilder ? null : (
      ((): StaticBoxBatcher | null => {
        const batchable: BatchableObject[] = []
        for (const obj of descriptor.objects) {
          if (isBatchableDescriptor(obj)) {
            batchable.push({ id: obj.id, descriptor: obj })
          }
        }
        if (batchable.length === 0) {
          return null
        }
        return createStaticBoxBatcher(batchable, {
          scene,
          createBucketMaterial,
          chunkSize: 64,
          cullDistance: 500,
        })
      })()
    )

  const camera = new THREE.PerspectiveCamera(
    CAMERA_FOV,
    initialWidth / initialHeight,
    0.1,
    2000,
  )

  const renderer = new THREE.WebGLRenderer({ antialias: true })
  // テクスチャ異方性フィルタの上限（デバイス依存、通常 16）。読み込む画像テクスチャ
  // （メッシュテクスチャ・面 Decal/Texture）へ適用し、斜め視線・遠距離での模様の
  // つぶれ・白モアレを抑える。
  const maxAnisotropy = renderer.capabilities.getMaxAnisotropy()
  // 画像テクスチャの生ロード（mesh/decal 共通）。three の TextureLoader を Promise 化する。
  const loadRawTexture = (url: string): Promise<THREE.Texture> =>
    new Promise((resolve, reject) => {
      new THREE.TextureLoader().load(url, resolve, undefined, reject)
    })
  // メッシュテクスチャは URL 単位で共有し、同一画像の重複 GPU アップロード
  // （texSubImage2D）を防ぐ。設定（colorSpace/wrap/anisotropy・カットアウト加工）は
  // ここで一括適用し、各 MeshPart の material には共有 Texture を差すだけにする。
  const meshTextureCache = createSharedTextureCache(
    loadRawTexture,
    (texture, cutout) => {
      texture.colorSpace = THREE.SRGBColorSpace
      // メッシュの UV.y は生値のまま（Roblox v1 の規約は three の既定 flipY=true と
      // 一致する。詳細は loadRobloxMesh.ts の parseV1）。
      texture.wrapS = THREE.RepeatWrapping
      texture.wrapT = THREE.RepeatWrapping
      // 斜め視線・遠距離での模様のつぶれ・モアレを抑える。
      texture.anisotropy = maxAnisotropy
      if (cutout) {
        // 透明部の白 RGB が mipmap で混ざり遠距離の葉が白い網状に見えるのを防ぐ。
        bleedCutoutTexture(texture)
      }
    },
  )
  // decal（面オーバーレイ画像）も URL+繰り返し有無の単位で共有する。タイル繰り返しは
  // 呼び出し側が UV へ織り込むため、ここでは wrap モードのみ variant で切り替える。
  const decalTextureCache = createSharedTextureCache(
    loadRawTexture,
    (texture, repeat) => {
      texture.colorSpace = THREE.SRGBColorSpace
      texture.anisotropy = maxAnisotropy
      const wrap = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping
      texture.wrapS = wrap
      texture.wrapT = wrap
    },
  )
  renderer.setPixelRatio(window.devicePixelRatio)
  renderer.setSize(initialWidth, initialHeight)
  // 色管理: リニア演算 → sRGB 出力を明示する（three 既定だが将来変更に備え固定）。
  // トーンマッピングは Neutral（Khronos PBR Neutral）を使う。ACES は鮮やかな原色を
  // くすませるが、Neutral は色相・彩度を保ったままハイライトだけ圧縮するため、
  // 子ども向けの鮮やかな Roblox 配色（原色多用）の白飛びを抑えつつ発色を保てる。
  THREE.ColorManagement.enabled = true
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.toneMapping = THREE.NeutralToneMapping
  // 実機スクリーンショットとの比較で全体的に暗かったため露出を上げる
  // （単一ノブで全ミッション一律に明るくなる。NeutralToneMapping が上限の
  // 白飛びを軽減する）。2.2 はユーザー要望（1.45 の約 1.5 倍）による引き上げ。
  renderer.toneMappingExposure = 2.2
  container.appendChild(renderer.domElement)

  // Roblox 風ライティング: 太陽風の平行光に、空/地の半球光と弱い環境光を加える。
  // 実機の屋外既定（明るい太陽 + 青空の照り返し）に寄せ、太陽をわずかに暖色・強めに、
  // 環境光をやや上げて影側（建物の陰・岩山の斜面）の潰れを防ぐ。トーンマッピングが
  // 上限の白飛びを抑えるので合計輝度が 1 を超えても破綻しない。
  // パフォーマンス優先で影は無効のまま。
  // 夜は太陽光を弱い青白い月光に差し替え、環境光も落として夜らしい陰影にする
  // （地形・オブジェクトが見える最低限の明るさは確保する）。
  const ambient = new THREE.AmbientLight(0xffffff, night ? 0.28 : 0.45)
  const directional = new THREE.DirectionalLight(
    night ? 0xaabfe0 : 0xfff4e0,
    night ? 0.6 : 1.0,
  )
  if (night) {
    // 月の方向から照らす（月スプライトの位置と光源方向を一致させる）。
    directional.position.set(
      MOON_LIGHT_DIRECTION.x * 40,
      MOON_LIGHT_DIRECTION.y * 40,
      MOON_LIGHT_DIRECTION.z * 40,
    )
  } else {
    directional.position.set(15, 30, 10)
  }
  const hemisphere =
    night ?
      new THREE.HemisphereLight(0x2a3a66, 0x10141f, 0.35)
    : new THREE.HemisphereLight(0xb5dbff, 0x4a7338, 0.5)
  scene.add(ambient)
  scene.add(directional)
  scene.add(hemisphere)

  // 地面グリッドは配置補助のためビルダー専用（クリック判定では無視する）。
  // おためしプレイでは Roblox 実機に無い要素であり、地面が y=0 以外のシーン
  // （例: RJF02 の谷地形）では空中に浮いて見えるため表示しない。
  const grid: THREE.GridHelper | null =
    enableBuilder ? new THREE.GridHelper(120, 60, 0xbcd4ee, 0xd6e6f7) : null
  if (grid) {
    scene.add(grid)
  }

  // Roblox Terrain（高さマップ）の曲面メッシュ + 水面。Blockly の操作対象では
  // ない背景なので、entries（オブジェクト管理）とは独立に持つ。
  const terrainView =
    descriptor.terrain ?
      createTerrainView(descriptor.terrain, descriptor.objects)
    : null
  if (terrainView) {
    scene.add(terrainView.group)
  }

  // 焼き込み静的メッシュ（Union の実形状など）。terrain と同じく背景扱い。
  // 制約: terrain と同様、遮蔽フェード（updateOcclusionFade）の対象外。
  // RJF05 の砦では壁・手すりは個別 Part（entries）なので従来どおりフェード
  // するが、Union 由来の床・内部構造がカメラとアバターの間に入った場合は
  // 透けない。専用のフェード経路（マテリアル複製 + 個別レイキャスト）は
  // 複雑さに見合う頻度で起きてから追加する。
  const staticMeshView =
    descriptor.staticMeshes ?
      createStaticMeshView(descriptor.staticMeshes)
    : null
  if (staticMeshView) {
    scene.add(staticMeshView.group)
  }

  // カメラフレーミング: 記述があれば採用、無ければ自動フィット。
  const framing =
    descriptor.camera ?
      {
        position: toVector3(descriptor.camera.position),
        target: toVector3(descriptor.camera.target),
      }
    : computeAutoFit(descriptor)
  camera.position.copy(framing.position)
  camera.lookAt(framing.target)

  const controls = new OrbitControls(camera, renderer.domElement)
  controls.target.copy(framing.target)
  controls.enableDamping = true
  // 自由視点での操作:
  //   - 左ドラッグ = 回転（orbit）
  //   - 右ドラッグ = 平行移動（pan）
  //   - 中ドラッグ / ホイール = ズーム
  // screenSpacePanning=true で「上にドラッグ＝ワールド上方向」となり直感的になる。
  controls.enablePan = true
  controls.screenSpacePanning = true
  controls.panSpeed = 1.0
  controls.mouseButtons = {
    LEFT: THREE.MOUSE.ROTATE,
    MIDDLE: THREE.MOUSE.DOLLY,
    RIGHT: THREE.MOUSE.PAN,
  }
  // タッチ操作: 1 指=回転、2 指=ピンチズーム+パン。
  controls.touches = {
    ONE: THREE.TOUCH.ROTATE,
    TWO: THREE.TOUCH.DOLLY_PAN,
  }
  // 右ドラッグでパンするときの context menu を抑制（ドラッグ妨害防止）。
  renderer.domElement.addEventListener('contextmenu', (event) => {
    event.preventDefault()
  })
  controls.update()

  // 実体エントリ（objectId -> MeshEntry）と、命中 Object3D -> objectId の逆引き。
  // AVATAR_ID は entries には入れず、別系統の avatarEntry で扱う。
  const entries = new Map<string, MeshEntry>()
  const objectIdByObject = new WeakMap<THREE.Object3D, string>()
  // 視点遮蔽でフェード中の部品 id。removeEntry が参照するため entries と同位置で宣言。
  const occludedIds = new Set<string>()
  // 視点遮蔽のためにバッチから個別メッシュへ一時降格中の静的 box id。
  // 降格中は canStayBatched=false で個別メッシュを保持し、滑らかにフェードする。
  const occlusionDemotedIds = new Set<string>()

  // 操作アバター（博士 VRM）。state にアバターが現れたときだけ生成する。
  let avatarEntry: VrmAvatarEntry | null = null
  let lastAnimateTime: number | null = null
  // 物理側のモーション状態取得 provider。PreviewPanel が AvatarController 生成後に注入する。
  // null の間は位置 Y 差分推定にフォールバックする（既存挙動を維持）。
  let avatarMotionStateProvider: ((nowMs: number) => AvatarMotionState) | null =
    null

  // カメラ視点モード。free が既定。アバター不在/ビルダー時は実効的に free。
  let cameraMode: CameraMode = 'free'
  // follow モードの距離倍率と first モードの FOV（モード切替を跨いで保持）。
  let followDistance = FOLLOW_DISTANCE_DEFAULT
  let firstFov = CAMERA_FOV
  // updateProjectionMatrix の不要呼び出しを避けるための直近適用済み FOV。
  let lastAppliedFov = CAMERA_FOV
  // follow モードの state。
  // - followYaw: カメラ方位（rad）。プレイヤー操作主導で、snap 時のみアバター向きへ
  //   初期化し以降は自動追従しない（cameraYawOffset とともに移動方向の基準になる）。
  // - followCamOffset: アバター位置→カメラ位置のオフセット（lerp のソース）。
  //   アバターの移動（歩行・落下）には剛体追従し、ドラッグ・ズーム・俯瞰切替など
  //   オフセット自体の変化だけを平滑化する。カメラの絶対位置を lerp すると落下時に
  //   カメラが上へ取り残され、注視点に引かれて視線が下へ倒れ込む（酔いの原因）。
  // - followNeedsSnap: 初回 / モード遷移直後にジャンプを避けるため、方位・位置を瞬間確定するフラグ。
  let followYaw = 0
  // 乗車中フラグ（renderLoop が motionState から毎フレーム更新）。乗車中は
  // followYaw の自動追従と引きの画角（VEHICLE_CAMERA_*）を有効にする。
  let cameraSeated = false
  // 乗車ズーム量（0..1）。乗降時に画角が滑らかに遷移するよう平滑化する。
  let seatedZoomAmp = 0
  let followCamOffset: THREE.Vector3 | null = null
  // lerp 先の一時ベクトル（render loop 内の毎フレーム allocation を避ける）。
  const followOffsetTarget = new THREE.Vector3()
  let followNeedsSnap = true
  // ユーザー操作で加えるカメラ視点オフセット（follow モード時にマウスドラッグで変える）。
  // アバターの向きが変わってもオフセットは保持され、ユーザーが選んだ角度を維持する。
  let cameraYawOffset = 0
  let cameraPitchOffset = 0
  // リスポーン検知用。motionState.respawnGeneration の増加でカメラ視点角度を
  // 背後初期へ戻す（死亡から復活したとき直前のドラッグ向きが残らないようにする）。
  let lastRespawnGeneration = 0
  // dev 専用: follow カメラの「見る角度」だけを固定オフセットする（既定 0）。
  // movementBasisYaw には加えないため、アバターの進行方向は変えずにカメラだけ
  // 横へ回り込ませられる（走行モーション調整画面の側面ビュー用）。
  let followViewYawOffset = 0
  // 停止中の俯瞰ビュー。停止ボタンで true、再生ボタンで false（PreviewPanel が制御）。
  // follow モードの距離・ピッチを上書きし、上から見下ろす遠目の視点へ引く。
  let overviewMode = false

  // GLB ローダ（ビルダー/プレイ問わず modelUrl があれば使用）。
  const gltfLoader = new GLTFLoader()

  /**
   * modelUrl があれば GLB を読み込み、container にぶら下げてプレースホルダ箱を
   * scene graph から外す（box は raycast 対象から外したいため visible では不十分）。
   * 失敗時は箱フォールバックのまま console.warn。
   *
   * dispose / destroy / 再読込時は entry の同一性と modelToken で stale な読込結果を
   * 検出し、orphan 化させずに破棄する。
   */
  /**
   * Roblox 独自 .mesh を読み込んで、part の Size に内接する Box にフィットさせて表示する。
   * Mesh ローカル座標のバウンディングから等比スケールを計算し、part の Size で
   * 包めるように正規化する。失敗時は box フォールバックのまま console.warn。
   */
  const loadRobloxMeshFor = (
    id: string,
    entry: MeshEntry,
    url: string,
    partSize: Vec3,
    textureUrl?: string,
    meshScale?: Vec3,
    meshOffset?: Vec3,
    textureCutout?: boolean,
  ): void => {
    entry.modelToken += 1
    const token = entry.modelToken
    loadRobloxMeshGeometry(url)
      .then((geometry) => {
        if (entries.get(id) !== entry || entry.modelToken !== token) {
          geometry.dispose()
          return
        }
        const material = new THREE.MeshStandardMaterial({
          color: 0xffffff,
          roughness: 0.7,
          metalness: 0,
        })
        // テクスチャがあれば albedo マップに適用する。Roblox はテクスチャ付き
        // MeshPart に Part.Color を乗算しない（テクスチャ表示が Color を上書き）
        // ため、読込成功時に color を白へ戻し、applyMaterialState の乗算も
        // スキップさせる（textureOverridesColor フラグ）。
        // ジオメトリ解決後に material が確定してから読み込む（同一 token/entry を再確認）。
        if (textureUrl !== undefined) {
          // 共有キャッシュ経由でロードする。同一 textureUrl を持つ多数の MeshPart で
          // Texture を 1 つに集約し、GPU への重複アップロードを避ける。テクスチャ自体
          // の設定（colorSpace/wrap/anisotropy・カットアウト加工）はキャッシュ側の
          // configure が適用済みで、ここでは material への割り当てだけ行う。
          meshTextureCache
            .load(textureUrl, textureCutout === true)
            .then((texture) => {
              if (entries.get(id) !== entry || entry.modelToken !== token) {
                // 共有 Texture は破棄しない（他部品が参照中。解放はキャッシュで一括）。
                return
              }
              material.map = texture
              // カットアウト系（SurfaceAppearance.AlphaMode=Transparency。例: 木の葉
              // カード）だけアルファ部を抜く。Roblox は素の TextureID のアルファを
              // 無視して不透明描画するため（例: 巨大樹の幹はアルファ 0 の単色テクセル
              // 参照）、一律に抜くと幹などが消えてしまう。
              if (textureCutout === true) {
                material.alphaTest = 0.5
              }
              // Part.Color の乗算を打ち消す（読込前に applyState 済みの場合）。初期色は
              // 退避し、学習者が色を明示変更したときだけ乗算で反映する（applyMaterialState
              // 参照。例: RJF11 の UFO 円盤への色転写）。
              material.userData.initialColor = material.color.getHexString()
              material.color.setHex(0xffffff)
              material.userData.textureOverridesColor = true
              material.needsUpdate = true
            })
            .catch((error: unknown) => {
              console.warn(
                `Failed to load mesh texture for "${id}" (${textureUrl}):`,
                error,
              )
            })
        }
        const mesh = new THREE.Mesh(geometry, material)
        // applyState が container.scale = Part.Size を一律に乗算する前提で、
        // mesh 側のスケールは「打ち消し」を織り込んで指定する:
        //   world_extent = container.scale × mesh.scale × mesh.bbox
        const bbox = geometry.boundingBox
        if (bbox) {
          if (meshScale) {
            // SpecialMesh(FileMesh): メッシュはネイティブ寸法 × Scale 倍率で描画され、
            // Part.Size には追従しない（Roblox 仕様）。1/Part.Size で打ち消した上で
            // Scale を乗せる。メッシュ原点を Part 中心へ置く（再センタリングしない）。
            // 体パーツ（脚・腕など）はメッシュ原点を基準に組み立てられているため、
            // bbox 中央寄せすると各パーツがずれて分離してしまう。SpecialMesh.Offset
            // （Part ローカル studs）は partSize の打ち消しを織り込んで反映する。
            const sx = meshScale.x / Math.max(0.001, partSize.x)
            const sy = meshScale.y / Math.max(0.001, partSize.y)
            const sz = meshScale.z / Math.max(0.001, partSize.z)
            mesh.scale.set(sx, sy, sz)
            const off = meshOffset ?? { x: 0, y: 0, z: 0 }
            mesh.position.set(
              off.x / Math.max(0.001, partSize.x),
              off.y / Math.max(0.001, partSize.y),
              off.z / Math.max(0.001, partSize.z),
            )
          } else {
            // MeshPart: Roblox では視覚メッシュは Part.Size の箱いっぱいに
            // 引き伸ばされる（Size を変えると見た目も追従する仕様）。
            // container.scale が Part.Size を乗算するため、mesh.bbox を単位箱へ
            // 正規化（1/bbox 寸法）して bbox 中心を Part 中心へ合わせれば、
            // world_extent = Part.Size になり実機と一致する。
            // 以前は「ネイティブ寸法描画」と誤解した実装で、Studio 上で
            // リサイズされた MeshPart（例: RJF01 のボス恐竜）が小さく・
            // 浮いて見えていた。
            const extentX = Math.max(0.001, bbox.max.x - bbox.min.x)
            const extentY = Math.max(0.001, bbox.max.y - bbox.min.y)
            const extentZ = Math.max(0.001, bbox.max.z - bbox.min.z)
            const sx = 1 / extentX
            const sy = 1 / extentY
            const sz = 1 / extentZ
            mesh.scale.set(sx, sy, sz)
            mesh.position.set(
              -((bbox.min.x + bbox.max.x) / 2) * sx,
              -((bbox.min.y + bbox.max.y) / 2) * sy,
              -((bbox.min.z + bbox.max.z) / 2) * sz,
            )
          }
        }
        const group = new THREE.Group()
        group.add(mesh)
        entry.model = group
        entry.container.remove(entry.box)
        entry.container.add(group)
        entry.modelMaterials.clear()
        entry.modelMaterials.add(material)
        entry.visualKey = undefined
        const currentState = world.getState().get(id)
        if (currentState) {
          applyState(entry, currentState)
        }
      })
      .catch((error: unknown) => {
        console.warn(`Failed to load Roblox mesh for "${id}" (${url}):`, error)
      })
  }

  const loadModelFor = (id: string, entry: MeshEntry, url: string): void => {
    entry.modelToken += 1
    const token = entry.modelToken
    gltfLoader.load(
      url,
      (gltf: GLTF) => {
        if (entries.get(id) !== entry || entry.modelToken !== token) {
          // 読込中に dispose / destroy / 再読込された → 破棄して何もしない。
          disposeModel(gltf.scene)
          return
        }
        entry.model = gltf.scene
        // プレースホルダ箱を scene graph から外す（geometry/material は dispose 時に解放）。
        entry.container.remove(entry.box)
        entry.container.add(gltf.scene)
        // 新モデルのマテリアル集合を 1 度だけ収集（applyState の毎回 traverse を回避）。
        entry.modelMaterials.clear()
        collectModelMaterials(gltf.scene, entry.modelMaterials)
        // visualKey をリセットして新マテリアルへ現在 state を確実に反映する。
        entry.visualKey = undefined
        const currentState = world.getState().get(id)
        if (currentState) {
          applyState(entry, currentState)
        }
      },
      undefined,
      (error: unknown) => {
        console.warn(`Failed to load model for "${id}" (${url}):`, error)
      },
    )
  }

  /**
   * objectId に対応するエントリを取得し、無ければ生成してシーンへ追加する。
   *
   * 視覚・transform は World 状態のみから構築するため、`descriptor.objects`
   * 由来かアバター（AVATAR_ID）かを問わず、World 状態に存在する id はすべて描画
   * する。destroy 後に reset で状態が復活した場合も、ここで再生成される。
   * 対応する記述に modelUrl があれば箱を生成しつつモデル読込を開始する。
   *
   * @param state 当該 id の現在状態。descriptor を持たない実行時生成オブジェクト
   *   （clone / ミッションビヘイビアの addObject）の形状解決に使う。
   */
  const ensureEntry = (id: string, state?: ObjectRuntimeState): MeshEntry => {
    const existing = entries.get(id)
    if (existing) {
      return existing
    }
    // GLB / Roblox .mesh バックのオブジェクトはスタッドを貼らない（ロード後に box は
    // scene graph から外されるため、placeholder にもスタッド不要）。
    const descriptor = descriptorById.get(id)
    const modelUrl = descriptor?.modelUrl
    const meshUrl = descriptor?.meshUrl
    const hasExternal = modelUrl !== undefined || meshUrl !== undefined
    // mesh/glb バックは box プレースホルダ（後でメッシュに差し替え）。それ以外は
    // descriptor の形状でジオメトリを作る。descriptor を持たない実行時生成
    // オブジェクトは World 状態の形状（spec.shape 由来）を使う。
    const shape: ShapeKind =
      hasExternal ? 'box' : (descriptor?.shape ?? state?.shape ?? 'box')
    // 円柱は size から軸（'x'=Cylinder パーツ / 'y'=CylinderMesh 由来）を推定する。
    const cylinderAxis =
      shape === 'cylinder' && descriptor ? cylinderAxisOf(descriptor.size) : 'x'
    // 天面にデカールを持つ部品（例: SpawnLocation）はスタッド模様を貼らず無地にする。
    // Roblox の SpawnLocation はスムーズな天面にデカールだけが載った見た目のため、
    // スタッドが透過部から覗くと実機と印象が変わってしまう。
    const hasTopDecal =
      descriptor?.decals?.some((decal) => decal.face === 'top') ?? false
    const entry = createMeshEntry(
      shape,
      !hasExternal && !hasTopDecal,
      cylinderAxis,
    )
    entry.hasDecals = (descriptor?.decals?.length ?? 0) > 0
    entries.set(id, entry)
    objectIdByObject.set(entry.container, id)
    objectIdByObject.set(entry.box, id)
    scene.add(entry.container)

    if (modelUrl) {
      loadModelFor(id, entry, modelUrl)
    } else if (meshUrl && descriptor) {
      loadRobloxMeshFor(
        id,
        entry,
        meshUrl,
        descriptor.size,
        descriptor.textureUrl,
        descriptor.meshScale,
        descriptor.meshOffset,
        descriptor.textureCutout,
      )
    } else if (
      descriptor?.decals &&
      (shape === 'box' || shape === 'wedge' || shape === 'cornerWedge')
    ) {
      // 外部メッシュでない box は面に Decal/Texture を貼る（GAP-DECAL）。
      // wedge / cornerWedge は全面投影の近似で貼る（RJF05 の屋根板など）。
      loadDecalsFor(
        entry,
        descriptor.decals,
        descriptor.size,
        decalTextureCache.load,
      )
    }
    // 面の固定テキスト看板（SurfaceGui > TextLabel 由来。例: RJS04 の Score 看板）は
    // Decal/Texture（画像）とは独立に貼れるため、上の分岐とは別に適用する。
    // 外部メッシュ（mesh/glb）はプレースホルダ box が後で差し替えられ看板が消えるため除外。
    if (descriptor?.surfaceText && shape === 'box' && !hasExternal) {
      loadSurfaceTextFor(
        entry,
        descriptor.surfaceText,
        descriptor.size,
        maxAnisotropy,
      )
    }
    return entry
  }

  /** エントリを完全に取り除く（ギズモ装着中なら外す）。 */
  const removeEntry = (id: string, entry: MeshEntry): void => {
    // 進行中のモデル読込を無効化（完了コールバックが orphan を add しないように）。
    entry.modelToken += 1
    if (selectedId === id) {
      setSelected(null)
    }
    if (entry.model) {
      entry.container.remove(entry.model)
    }
    scene.remove(entry.container)
    disposeMeshEntry(entry)
    entries.delete(id)
    occludedIds.delete(id)
    // 降格中の box が destroy 等で消えたときに stale id を残さない。
    occlusionDemotedIds.delete(id)
  }

  /**
   * AVATAR_ID 用: 博士 VRM アバターを生成してシーンへ追加する（無ければ）。
   * 生成直後は placeholder 箱が表示され、VRM 読込成功時に置き換える。
   */
  const ensureAvatar = (state: ObjectRuntimeState): VrmAvatarEntry => {
    if (avatarEntry) {
      return avatarEntry
    }
    const entry = createVrmAvatarEntry()
    // 移動検知の初期化（生成直後は静止扱い）。垂直方向はジャンプ判定で利用する。
    entry.prevX = state.position.x
    entry.prevZ = state.position.z
    entry.prevY = state.position.y
    avatarEntry = entry
    scene.add(entry.group)

    // 非同期に VRM を読み込む。dispose/再生成競合は loadToken で破棄。
    const token = entry.loadToken
    const loader = new GLTFLoader()
    loader.register((parser) => new VRMLoaderPlugin(parser))
    loader.load(
      HAKASE_VRM_URL,
      (gltf: GLTF) => {
        const vrm = extractVrm(gltf.userData)
        if (avatarEntry !== entry || entry.loadToken !== token) {
          // 既に破棄/再生成された → 取り込まず即解放（VRM なら VRM として、
          // 無ければ glTF シーンとして解放）。stale なので placeholder は触らない。
          if (vrm) {
            VRMUtils.deepDispose(vrm.scene)
          } else {
            disposeModel(gltf.scene)
          }
          return
        }
        if (!vrm) {
          console.warn(
            `VRM payload missing in glTF userData (${HAKASE_VRM_URL})`,
          )
          // VRM が取れない → glTF を解放し、フォールバックとして placeholder を表示する。
          disposeModel(gltf.scene)
          entry.placeholderMesh.visible = true
          return
        }
        // メモリ最適化（VRM 推奨）と、Skinned メッシュのフラスタムカリングを無効化
        // （関節アニメ時のバウンディングずれによる pop-out 防止）。
        VRMUtils.removeUnnecessaryVertices(vrm.scene)
        VRMUtils.removeUnnecessaryJoints(vrm.scene)
        vrm.scene.traverse((obj: THREE.Object3D) => {
          obj.frustumCulled = false
        })

        // 素のバウンディングを計測してスケール計算と足元合わせに使う。
        const box = new THREE.Box3().setFromObject(vrm.scene)
        const height = Math.max(0.01, box.max.y - box.min.y)

        // T ポーズだと腕が水平に伸びて違和感があるので、humanoid bone を回して
        // 自然な A ポーズ（腕を斜め下）に補正する。VRM 1.0 humanoid の正規化骨は
        // モデル個体差を吸収しており、回転軸も VRM 仕様で揃っている。
        applyRestPoseAdjustments(vrm)

        entry.vrm = vrm
        entry.modelMeasuredHeight = height
        entry.modelMinY = box.min.y
        ;(window as unknown as { __avatarEntry?: unknown }).__avatarEntry =
          entry
        // 走りアニメ用に humanoid 骨をキャッシュ。骨が欠けている VRM でも安全に縮退する。
        const humanoid = vrm.humanoid
        if (humanoid) {
          entry.leftUpperArm = humanoid.getNormalizedBoneNode('leftUpperArm')
          entry.rightUpperArm = humanoid.getNormalizedBoneNode('rightUpperArm')
          entry.leftUpperLeg = humanoid.getNormalizedBoneNode('leftUpperLeg')
          entry.rightUpperLeg = humanoid.getNormalizedBoneNode('rightUpperLeg')
          entry.leftLowerLeg = humanoid.getNormalizedBoneNode('leftLowerLeg')
          entry.rightLowerLeg = humanoid.getNormalizedBoneNode('rightLowerLeg')
          entry.leftFoot = humanoid.getNormalizedBoneNode('leftFoot')
          entry.rightFoot = humanoid.getNormalizedBoneNode('rightFoot')
          entry.leftLowerArm = humanoid.getNormalizedBoneNode('leftLowerArm')
          entry.rightLowerArm = humanoid.getNormalizedBoneNode('rightLowerArm')
          entry.head = humanoid.getNormalizedBoneNode('head')
          entry.neck = humanoid.getNormalizedBoneNode('neck')
          // 指関節と拳の回転量（applyRestPoseAdjustments と同じ符号規約）。
          // 遊泳中に拳を開く（指を伸ばす）アニメで毎フレーム参照する。
          entry.fingerJoints = []
          for (const pair of FIST_BONE_PAIRS) {
            const leftBone = humanoid.getNormalizedBoneNode(pair.left)
            if (leftBone) {
              entry.fingerJoints.push({
                bone: leftBone,
                restY: 0,
                restZ: pair.bend,
              })
            }
            const rightBone = humanoid.getNormalizedBoneNode(pair.right)
            if (rightBone) {
              entry.fingerJoints.push({
                bone: rightBone,
                restY: 0,
                restZ: -pair.bend,
              })
            }
          }
          for (const pair of FIST_THUMB_PAIRS) {
            const leftBone = humanoid.getNormalizedBoneNode(pair.left)
            if (leftBone) {
              entry.fingerJoints.push({
                bone: leftBone,
                restY: FIST_THUMB_CURL_Y,
                restZ: FIST_THUMB_BEND_Z,
              })
            }
            const rightBone = humanoid.getNormalizedBoneNode(pair.right)
            if (rightBone) {
              entry.fingerJoints.push({
                bone: rightBone,
                restY: -FIST_THUMB_CURL_Y,
                restZ: -FIST_THUMB_BEND_Z,
              })
            }
          }
          // 診断: VRM ごとに骨欠落の可能性があるので、無い骨を起動時にログ通知する。
          const bonePairs: ReadonlyArray<
            readonly [string, THREE.Object3D | null]
          > = [
            ['leftUpperArm', entry.leftUpperArm],
            ['rightUpperArm', entry.rightUpperArm],
            ['leftLowerArm', entry.leftLowerArm],
            ['rightLowerArm', entry.rightLowerArm],
            ['leftUpperLeg', entry.leftUpperLeg],
            ['rightUpperLeg', entry.rightUpperLeg],
            ['leftLowerLeg', entry.leftLowerLeg],
            ['rightLowerLeg', entry.rightLowerLeg],
          ]
          const missing = bonePairs
            .filter(([, bone]) => bone === null)
            .map(([name]) => name)
          if (missing.length > 0) {
            console.warn(
              '[VRM] missing humanoid bones (animation may be skipped):',
              missing,
            )
          }
        }
        entry.modelRoot.add(vrm.scene)
        entry.modelRoot.visible = true

        // 現行 scaleH を VRM 反映後の正しいスケール・足元合わせで再適用。
        if (entry.scaleHKey !== null) {
          layoutVrmAvatar(entry, entry.scaleHKey)
        }
        // placeholder を scene graph から外す（geometry/material は dispose 時に解放）。
        entry.group.remove(entry.placeholderMesh)
      },
      undefined,
      (error: unknown) => {
        // 失敗時はフォールバックとして placeholder を表示する（VRM は未追加のまま）。
        console.warn(`Failed to load VRM avatar (${HAKASE_VRM_URL}):`, error)
        // 破棄/再生成済みなら触らない（stale callback）。
        if (avatarEntry === entry && entry.loadToken === token) {
          entry.placeholderMesh.visible = true
        }
      },
    )
    return entry
  }

  /** AVATAR_ID 用: VRM アバターを除去・解放する。 */
  const removeAvatar = (): void => {
    if (!avatarEntry) {
      return
    }
    scene.remove(avatarEntry.group)
    disposeVrmAvatarEntry(avatarEntry)
    avatarEntry = null
  }

  /**
   * 実行時 state が今もバッチ表現可能か。バケットは「不透明・descriptor の単一
   * マテリアル・非 Neon」前提なので、looks_seeThrough / setMaterialTo 等で崩れたら
   * 個別メッシュへ demote する。非表示は setState 側で退避するため可とみなす。
   */
  const canStayBatched = (id: string, state: ObjectRuntimeState): boolean => {
    // 遮蔽フェードのため個別メッシュへ降格中は、syncState で再バッチ化させない。
    if (occlusionDemotedIds.has(id)) {
      return false
    }
    if (!state.visible) {
      return true
    }
    const bucketMaterial = descriptorById.get(id)?.material ?? 'Plastic'
    return (
      state.transparency === 0 &&
      state.material === bucketMaterial &&
      !EMISSIVE_COLOR_MATERIALS.has(state.material)
    )
  }

  /**
   * World 状態をエントリへ同期する。
   * - AVATAR_ID は博士スプライト（avatarEntry）へ反映。
   * - その他の id は MeshEntry／静的 box バッチャへ反映（必要なら生成）。
   * - 状態 Map から消えた（destroy 済み）オブジェクトはシーンから除去し dispose。
   */
  const syncState = (states: ReadonlyMap<string, ObjectRuntimeState>): void => {
    let batchedSeen = 0
    for (const [id, state] of states) {
      if (id === AVATAR_ID) {
        applyVrmAvatarState(ensureAvatar(state), state)
      } else if (staticBoxBatcher?.has(id)) {
        // 実行時に半透明化・material 変更・Neon 化された静的 box は、不透明・単一
        // マテリアル前提のバッチでは正しく描けない。その場合は個別メッシュへ demote
        // する（バッチ可能に戻ったら次回 setState で再びバッチへ戻す）。
        if (canStayBatched(id, state)) {
          const demoted = entries.get(id)
          if (demoted) {
            removeEntry(id, demoted)
          }
          staticBoxBatcher.setState(id, state)
          batchedSeen += 1
        } else {
          staticBoxBatcher.remove(id)
          applyState(ensureEntry(id, state), state)
        }
      } else {
        applyState(ensureEntry(id, state), state)
      }
    }
    // バッチ対象が states から減った（destroy された）ときだけ照合して解放する。
    // 通常フレームは size と一致するので O(batched) の走査を避ける。
    if (staticBoxBatcher && batchedSeen < staticBoxBatcher.size) {
      staticBoxBatcher.reconcile(states)
    }
    for (const [id, entry] of entries) {
      if (!states.has(id)) {
        removeEntry(id, entry)
      }
    }
    if (avatarEntry && !states.has(AVATAR_ID)) {
      removeAvatar()
    }
  }

  // --- ビルダー: 選択＋トランスフォームギズモ -------------------------------

  let selectedId: string | null = null
  let transformControls: TransformControls | null = null
  let gizmoHelper: THREE.Object3D | null = null

  const notifyTransform = (id: string, entry: MeshEntry): void => {
    const onTransform = options?.onObjectTransform
    if (!onTransform) {
      return
    }
    // box は単位立方体×scale なので scale=size。
    const { position, scale, rotation } = entry.container
    onTransform(id, {
      position: { x: position.x, y: position.y, z: position.z },
      size: { x: scale.x, y: scale.y, z: scale.z },
      rotationY: rotation.y,
    })
  }

  const setSelected = (objectId: string | null): void => {
    if (!enableBuilder || !transformControls) {
      return
    }
    // 操作アバターはビルダー編集対象ではない（invariant）。API 経由でも選択不可。
    if (objectId === AVATAR_ID) {
      setSelected(null)
      return
    }
    if (objectId === null) {
      transformControls.detach()
      if (selectedId !== null) {
        selectedId = null
        options?.onObjectSelect?.(null)
      }
      return
    }
    const entry = entries.get(objectId)
    if (!entry) {
      // 存在しない id を渡された場合は選択解除（古いギズモを残さない）。
      setSelected(null)
      return
    }
    transformControls.attach(entry.container)
    if (selectedId !== objectId) {
      selectedId = objectId
      options?.onObjectSelect?.(objectId)
    }
  }

  const setGizmoMode = (mode: GizmoMode): void => {
    if (!transformControls) {
      return
    }
    transformControls.setMode(mode)
    // 契約は Y 軸回転のみ（types.ts）。rotate では X/Z ハンドルを隠し、
    // それ以外（translate/scale）では全軸を表示する。
    const showXZ = mode !== 'rotate'
    transformControls.showX = showXZ
    transformControls.showZ = showXZ
    transformControls.showY = true
  }

  if (enableBuilder) {
    transformControls = new TransformControls(camera, renderer.domElement)
    gizmoHelper = transformControls.getHelper()
    scene.add(gizmoHelper)
    // ギズモのドラッグ中は OrbitControls を無効化し、終了時に「元の状態」へ戻す。
    transformControls.addEventListener('dragging-changed', (event) => {
      if (event.value === true) {
        // ドラッグ中およびその直後の pointerup での誤選択解除を抑止する。
        gizmoInteractionThisCycle = true
        orbitEnabledBeforeDrag = controls.enabled
        controls.enabled = false
      } else {
        controls.enabled = orbitEnabledBeforeDrag
      }
    })
    // 操作中の transform 変化を通知。
    transformControls.addEventListener('objectChange', () => {
      if (selectedId === null) {
        return
      }
      const entry = entries.get(selectedId)
      if (entry) {
        // Y 軸のみ契約のため、ギズモ操作で混入し得る X/Z 回転を 0 に戻す。
        entry.container.rotation.x = 0
        entry.container.rotation.z = 0
        notifyTransform(selectedId, entry)
      }
    })
  }

  // --- 同期・購読 ----------------------------------------------------------

  // 初期表示の同期と以降の購読。
  syncState(world.getState())
  const unsubscribe = world.subscribe(syncState)

  // --- ポインタ操作（プレイ: クリック / ビルダー: 選択） --------------------

  const raycaster = new THREE.Raycaster()
  const pointer = new THREE.Vector2()
  let pointerDownX = 0
  let pointerDownY = 0
  // 当該ポインタ操作がギズモ操作だったか（builder の空クリック誤判定を抑止）。
  let gizmoInteractionThisCycle = false
  // 追跡中のポインタ ID。副ポインタを無視し、cancel/blur で確実にリセットする。
  let activePointerId: number | null = null
  // 追跡中ポインタの押下ボタン。クリック（選択/onClicked）は左ボタンに限定し、
  // 右ボタン（follow 視点回転）での誤発火を防ぐ。
  let activePointerButton: number | null = null
  // ギズモドラッグ開始前の OrbitControls.enabled（ドラッグ終了/中断時に復帰）。
  let orbitEnabledBeforeDrag = controls.enabled

  // ギズモドラッグ中に pointer が中断された場合、ドラッグを明示終了して
  // OrbitControls の enabled を復帰させる（stuck 状態を防ぐ）。
  const endTransformCycle = (): void => {
    if (!transformControls) {
      return
    }
    if (transformControls.dragging || transformControls.axis !== null) {
      transformControls.pointerUp(null)
    }
    controls.enabled = orbitEnabledBeforeDrag
  }

  const resetPointerCycle = (endTransform = false): void => {
    if (endTransform) {
      endTransformCycle()
    }
    activePointerId = null
    activePointerButton = null
    gizmoInteractionThisCycle = false
  }

  const isClick = (event: PointerEvent): boolean => {
    const movedX = Math.abs(event.clientX - pointerDownX)
    const movedY = Math.abs(event.clientY - pointerDownY)
    return movedX <= CLICK_DRAG_THRESHOLD && movedY <= CLICK_DRAG_THRESHOLD
  }

  const updatePointer = (event: PointerEvent): void => {
    const rect = renderer.domElement.getBoundingClientRect()
    pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
    raycaster.setFromCamera(pointer, camera)
  }

  /** 命中した Object3D（子含む）から所属 objectId を辿る。 */
  const resolveObjectId = (object: THREE.Object3D): string | undefined => {
    let current: THREE.Object3D | null = object
    while (current) {
      const id = objectIdByObject.get(current)
      if (id !== undefined) {
        return id
      }
      current = current.parent
    }
    return undefined
  }

  // --- 視点とアバターの間の遮蔽物を半透明化（オクルージョンフェード）---------
  // 三人称(follow)/自由視点で、カメラとアバターの間に入った部品を一時的に透かして
  // アバターを見失わないようにする。一人称はアバター非表示なので対象外。
  const occlusionRaycaster = new THREE.Raycaster()
  // raycast 候補・ヒット配列は毎フレーム使い回して allocation を避ける。
  const occlusionTargets: THREE.Object3D[] = []
  const occlusionHits: THREE.Intersection[] = []
  const _occTarget = new THREE.Vector3()
  const _occDir = new THREE.Vector3()
  const _occNormalMatrix = new THREE.Matrix3()
  const _occWorldNormal = new THREE.Vector3()
  const _occInstanceMatrix = new THREE.Matrix4()
  const _occWorldMatrix = new THREE.Matrix4()
  /**
   * 地面とみなすヒット面のワールド法線 Y しきい値。坂・床の上面（上向きの面）は
   * 遮蔽フェードの対象にしない。坂を下る際は追従カメラが背後の斜面を掠めて
   * レイが地面にヒットし、立っている坂全体が透けてしまう（RJF02 のすべり台）。
   * 地面を透かしてもアバターの視認には寄与しないため除外してよい。
   */
  const OCCLUSION_GROUND_NORMAL_Y = 0.7
  let occlusionFrame = 0
  let cullFrame = 0

  // フェード適用。値が変わるときだけ mutate して material version churn を避ける。
  const fadeEntry = (entry: MeshEntry): void => {
    const fade = (material: THREE.Material): void => {
      if (
        material.transparent &&
        material.opacity === OCCLUSION_OPACITY &&
        !material.depthWrite
      ) {
        return
      }
      material.transparent = true
      material.opacity = OCCLUSION_OPACITY
      material.depthWrite = false
      material.needsUpdate = true
    }
    for (const material of entry.boxMaterials) fade(material)
    for (const material of entry.modelMaterials) fade(material)
    for (const material of entry.decalMaterials) fade(material)
  }

  // World 状態から不透明度などを再適用して元へ戻す（visualKey 無効化で強制再適用）。
  const restoreEntry = (id: string, entry: MeshEntry): void => {
    entry.visualKey = undefined
    const state = world.getState().get(id)
    if (state) {
      applyState(entry, state)
    }
    // デカールオーバーレイは applyState の対象外なので個別に元へ戻す
    // （alphaTest は維持。フェードで立てた transparent/opacity/depthWrite を解除）。
    // Transparency 付き Decal/Texture は生成時の基準値（baseOpacity 等）へ戻し、
    // 一律不透明化で固有の透過が失われないようにする。
    for (const material of entry.decalMaterials) {
      const baseTransparent = material.userData.baseTransparent === true
      const baseOpacity =
        typeof material.userData.baseOpacity === 'number' ?
          material.userData.baseOpacity
        : 1
      if (
        material.transparent !== baseTransparent ||
        material.opacity !== baseOpacity ||
        !material.depthWrite
      ) {
        material.transparent = baseTransparent
        material.opacity = baseOpacity
        material.depthWrite = true
        material.needsUpdate = true
      }
    }
  }

  // バッチ済み静的 box を個別メッシュへ降格して滑らかにフェードする。
  // バッチインスタンスを隠し、descriptor から個別メッシュを生成して fadeEntry する。
  const demoteBatchedForOcclusion = (id: string): void => {
    const state = world.getState().get(id)
    if (!state) {
      return
    }
    occlusionDemotedIds.add(id)
    staticBoxBatcher?.remove(id)
    const entry = ensureEntry(id, state)
    applyState(entry, state)
    fadeEntry(entry)
  }

  // 降格していた静的 box を個別メッシュから外し、再びバッチ描画へ戻す。
  const restoreBatchedFromOcclusion = (id: string): void => {
    occlusionDemotedIds.delete(id)
    const entry = entries.get(id)
    if (entry) {
      removeEntry(id, entry)
    }
    const state = world.getState().get(id)
    if (state) {
      staticBoxBatcher?.setState(id, state)
    }
  }

  const restoreAllOccluded = (): void => {
    for (const id of occludedIds) {
      const entry = entries.get(id)
      if (entry) restoreEntry(id, entry)
    }
    occludedIds.clear()
    // バッチ降格していた box を再バッチ化する（コピーを回して反復中の変更を避ける）。
    for (const id of [...occlusionDemotedIds]) {
      restoreBatchedFromOcclusion(id)
    }
  }

  // 遮蔽候補 = 可視かつ現在の不透明度がフェード目標(0.25)より高い部品のみ。
  // これで透明トリガーゾーン等を 0.25 にして逆に見せてしまうのを防ぎ、
  // さらに raycast 対象数も大きく絞れる（巨大シーンの負荷低減）。
  const collectOcclusionTargets = (): void => {
    occlusionTargets.length = 0
    const states = world.getState()
    for (const [id, entry] of entries) {
      const state = states.get(id)
      if (
        state &&
        state.visible &&
        1 - state.transparency > OCCLUSION_OPACITY
      ) {
        occlusionTargets.push(entry.container)
      }
    }
    // バッチ済み静的 box（可視チャンクのみ）も遮蔽候補に含める。ヒットした
    // インスタンスは降格して個別メッシュとしてフェードする。
    if (staticBoxBatcher) {
      for (const mesh of staticBoxBatcher.meshes) {
        if (mesh.visible) occlusionTargets.push(mesh)
      }
    }
  }

  const updateOcclusionFade = (): void => {
    // アバター不在 or 一人称（アバター非表示）ではフェード不要。全復元して終了。
    if (!avatarEntry || getEffectiveCameraMode() === 'first') {
      restoreAllOccluded()
      return
    }
    const scaleH = avatarEntry.scaleHKey ?? 1
    // follow の lookAt（ay + 1.5*scaleH）と同じ注視点をレイ先にする。
    _occTarget.set(
      avatarEntry.group.position.x,
      avatarEntry.group.position.y + 1.5 * scaleH,
      avatarEntry.group.position.z,
    )
    _occDir.copy(_occTarget).sub(camera.position)
    const distance = _occDir.length()
    // アバター手前のマージンは体格（scaleH）に比例させる。
    const margin = OCCLUSION_AVATAR_MARGIN * scaleH
    if (distance <= margin) {
      // カメラがアバターに密着（ズームイン等）。フェード対象なし。
      restoreAllOccluded()
      return
    }
    _occDir.normalize()
    occlusionRaycaster.set(camera.position, _occDir)
    occlusionRaycaster.near = 0
    occlusionRaycaster.far = distance - margin
    collectOcclusionTargets()
    occlusionHits.length = 0
    occlusionRaycaster.intersectObjects(occlusionTargets, true, occlusionHits)
    const next = new Set<string>()
    const nextBatch = new Set<string>()
    for (const hit of occlusionHits) {
      // 上向きの面（坂・床の上面）へのヒットは「地面」であり遮蔽ではない。
      // フェードすると立っている坂や床ごと透けるため除外する。
      if (hit.face) {
        // InstancedMesh は姿勢が instanceMatrix 側にあるため合成してから
        // 法線変換する（matrixWorld 単体だと回転した box を誤判定する）。
        if (
          hit.object instanceof THREE.InstancedMesh &&
          hit.instanceId !== undefined
        ) {
          hit.object.getMatrixAt(hit.instanceId, _occInstanceMatrix)
          _occWorldMatrix.multiplyMatrices(
            hit.object.matrixWorld,
            _occInstanceMatrix,
          )
          _occNormalMatrix.getNormalMatrix(_occWorldMatrix)
        } else {
          _occNormalMatrix.getNormalMatrix(hit.object.matrixWorld)
        }
        _occWorldNormal
          .copy(hit.face.normal)
          .applyMatrix3(_occNormalMatrix)
          .normalize()
        if (_occWorldNormal.y > OCCLUSION_GROUND_NORMAL_Y) {
          continue
        }
      }
      // バッチ済み InstancedMesh は instanceId 付きで当たる。先にバッチを逆引きする。
      if (staticBoxBatcher && hit.instanceId !== undefined) {
        const batchId = staticBoxBatcher.resolveObjectId(
          hit.object,
          hit.instanceId,
        )
        if (batchId !== null) {
          nextBatch.add(batchId)
          continue
        }
      }
      const id = resolveObjectId(hit.object)
      if (id === undefined) {
        continue
      }
      // 既に降格中の box は個別メッシュとして当たるが、引き続きバッチ扱いで保持する。
      if (occlusionDemotedIds.has(id)) {
        nextBatch.add(id)
      } else {
        next.add(id)
      }
    }
    // 遮蔽中の個別部品をフェード（毎回再適用し、World 更新で戻ってもすぐ透かす）。
    for (const id of next) {
      const entry = entries.get(id)
      if (entry) fadeEntry(entry)
    }
    // 遮蔽中のバッチ box は個別メッシュへ降格して滑らかにフェードする。
    for (const id of nextBatch) {
      if (occlusionDemotedIds.has(id)) {
        const entry = entries.get(id)
        if (entry) fadeEntry(entry)
      } else {
        demoteBatchedForOcclusion(id)
      }
    }
    // 遮蔽から外れた個別部品は元へ戻す。
    for (const id of occludedIds) {
      if (!next.has(id)) {
        const entry = entries.get(id)
        if (entry) restoreEntry(id, entry)
      }
    }
    occludedIds.clear()
    for (const id of next) {
      occludedIds.add(id)
    }
    // 遮蔽から外れたバッチ降格は再バッチ化する（コピーを回して反復中の変更を避ける）。
    for (const id of [...occlusionDemotedIds]) {
      if (!nextBatch.has(id)) {
        restoreBatchedFromOcclusion(id)
      }
    }
  }

  // capture フェーズ: 追跡ポインタを確定し、副ポインタは controls へ渡る前に遮断する。
  const handlePointerDownCapture = (event: PointerEvent): void => {
    if (activePointerId !== null) {
      // 既に主ポインタ追跡中 → 副ポインタは OrbitControls/TransformControls にも
      // 届かせない（ギズモ誤 drag・誤フラグを防ぐ）。
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }
    activePointerId = event.pointerId
    activePointerButton = event.button
    pointerDownX = event.clientX
    pointerDownY = event.clientY
    gizmoInteractionThisCycle = false
  }

  // bubble フェーズ: controls が axis を更新した後にギズモ把持状態を読む。
  const handlePointerDown = (event: PointerEvent): void => {
    if (activePointerId !== event.pointerId) {
      return
    }
    // 押下時にギズモの軸へホバー/把持していれば、この操作はギズモ操作とみなす。
    // （TransformControls は hover/把持中も axis に値が入る）
    gizmoInteractionThisCycle =
      transformControls !== null && transformControls.axis !== null
  }

  /** プレイ時クリック/ホバーの raycast 候補（可視・不透明・clickable な部品）を集める。 */
  const collectClickableObjects = (): THREE.Object3D[] => {
    const states = world.getState()
    const clickableObjects: THREE.Object3D[] = []
    for (const [id, entry] of entries) {
      const state = states.get(id)
      if (state && isClickableState(state)) {
        clickableObjects.push(entry.container)
      }
    }
    // バッチ化された静的 box も raycast 候補に含める（クリック可否はヒット後に判定）。
    // Raycaster は visible を見ないため、距離カリングで非表示のチャンクは除外する。
    if (staticBoxBatcher) {
      for (const mesh of staticBoxBatcher.meshes) {
        if (mesh.visible) {
          clickableObjects.push(mesh)
        }
      }
    }
    return clickableObjects
  }

  /**
   * ヒットを近い順に走査し、最初の「クリック可能」オブジェクトの objectId を返す
   * （バッチの非クリック装飾が手前にあっても奥のクリック対象を拾う）。
   */
  const firstClickableHit = (
    hits: THREE.Intersection[],
  ): string | undefined => {
    const states = world.getState()
    for (const hit of hits) {
      const objectId =
        hit.object instanceof THREE.InstancedMesh ?
          hit.instanceId !== undefined ?
            (staticBoxBatcher?.resolveObjectId(hit.object, hit.instanceId) ??
            undefined)
          : undefined
        : resolveObjectId(hit.object)
      if (objectId === undefined || objectId === null) {
        continue
      }
      const state = states.get(objectId)
      if (state && isClickableState(state)) {
        return objectId
      }
    }
    return undefined
  }

  /** プレイ時クリック: 可視・不透明・clickable なオブジェクトのみ命中対象。 */
  const handlePlayClick = (event: PointerEvent): void => {
    const onClick = options?.onObjectClick
    if (!onClick) {
      return
    }
    updatePointer(event)
    const objectId = firstClickableHit(
      raycaster.intersectObjects(collectClickableObjects(), true),
    )
    if (objectId !== undefined) {
      onClick(objectId)
    }
  }

  /**
   * ビルダー時クリック: 可視オブジェクトを選択（空クリックで解除）。
   * 操作アバター（AVATAR_ID）は Explorer 側でも除外されており、ビルダーの
   * 編集対象ではないため、レイキャスト候補からも除外する。
   */
  const handleBuilderClick = (event: PointerEvent): void => {
    updatePointer(event)
    const states = world.getState()
    const selectableObjects: THREE.Object3D[] = []
    for (const [id, entry] of entries) {
      if (id === AVATAR_ID) {
        continue
      }
      const state = states.get(id)
      if (state && state.visible) {
        selectableObjects.push(entry.container)
      }
    }
    const hits = raycaster.intersectObjects(selectableObjects, true)
    const objectId = hits[0] ? resolveObjectId(hits[0].object) : undefined
    setSelected(objectId ?? null)
  }

  const handlePointerUp = (event: PointerEvent): void => {
    // 追跡中ポインタ以外の up は無視。
    if (activePointerId !== event.pointerId) {
      return
    }
    // ギズモ操作（軸クリック/微小ドラッグ含む）だった場合は選択処理をスキップ。
    // 本当の空クリックでのみ deselect させるための抑止。
    const wasGizmoInteraction =
      gizmoInteractionThisCycle ||
      (transformControls !== null && transformControls.dragging)
    // クリック（選択/onClicked）は左ボタンのみ。右ボタンは follow 視点回転用で、
    // 移動量が小さい右ドラッグ終了をクリック扱いすると誤発火する。
    const wasPrimaryButton = activePointerButton === 0
    resetPointerCycle()
    if (wasGizmoInteraction || !wasPrimaryButton) {
      return
    }
    if (!isClick(event)) {
      return
    }
    if (enableBuilder) {
      handleBuilderClick(event)
    } else {
      handlePlayClick(event)
    }
  }

  // pointerup が来ない/中断された場合に追跡状態を取り残さない。
  // 進行中のギズモドラッグも明示終了し、OrbitControls を復帰させる。
  const handlePointerCancel = (event: PointerEvent): void => {
    if (activePointerId === event.pointerId) {
      resetPointerCycle(true)
    }
  }
  const handleWindowBlur = (): void => {
    resetPointerCycle(true)
  }

  const canvas = renderer.domElement
  // capture を先に登録（controls の listener より前に副ポインタを遮断するため）。
  canvas.addEventListener('pointerdown', handlePointerDownCapture, true)
  canvas.addEventListener('pointerdown', handlePointerDown)
  canvas.addEventListener('pointerup', handlePointerUp)
  canvas.addEventListener('pointercancel', handlePointerCancel)
  // window blur の購読は、PIP で keyboardTarget が別ウインドウへ移動しても
  // 追従できるよう、末尾の startKeyboardTargetLifecycle でまとめて行う。

  // リサイズ追従。
  const resizeObserver = new ResizeObserver(() => {
    const width = Math.max(1, container.clientWidth)
    const height = Math.max(1, container.clientHeight)
    camera.aspect = width / height
    camera.updateProjectionMatrix()
    renderer.setSize(width, height)
  })
  resizeObserver.observe(container)

  // --- カメラ視点モード（free / follow / first） ---------------------------

  /**
   * 適用すべき実効モードを返す。アバター不在 / ビルダーモードでは free に
   * フォールバックする（avatarEntry/scaleHKey が無いと位置算出できないため）。
   * cameraMode 自体は保持するので、アバター登場後/プレイ復帰後に即適用される。
   */
  const getEffectiveCameraMode = (): CameraMode => {
    if (enableBuilder) {
      return 'free'
    }
    if (!avatarEntry || avatarEntry.scaleHKey === null) {
      return 'free'
    }
    return cameraMode
  }

  /** 毎フレーム描画前にカメラ位置・lookAt とコントロール有効/無効を反映する。 */
  /** FOV を必要なときだけ反映する（updateProjectionMatrix の無駄呼び出し防止）。 */
  const applyFov = (targetFov: number): void => {
    if (lastAppliedFov === targetFov) {
      return
    }
    camera.fov = targetFov
    lastAppliedFov = targetFov
    camera.updateProjectionMatrix()
  }

  /**
   * カメラ処理が適用してよいアバターの表示状態。死亡演出（奈落落下= void）で
   * 非表示にしている間は、free/follow の毎フレーム再表示や first 解除時の
   * 復帰処理が上書きして「消えたはずのアバターが見える」のを防ぐ。
   */
  const avatarVisibleUnlessVoidDeath = (entry: VrmAvatarEntry): boolean =>
    entry.lastDeathKind !== 'void'

  const updateCamera = (dt: number): void => {
    const effective = getEffectiveCameraMode()
    if (effective === 'free') {
      controls.enabled = true
      if (avatarEntry) {
        avatarEntry.group.visible = avatarVisibleUnlessVoidDeath(avatarEntry)
      }
      // free/follow は既定 FOV に揃える（first から復帰した場合に元へ戻す）。
      applyFov(CAMERA_FOV)
      return
    }

    // follow / first はアバター必須（getEffectiveCameraMode で保証）。
    if (!avatarEntry) {
      return
    }
    const scaleH = avatarEntry.scaleHKey
    if (scaleH === null) {
      return
    }
    controls.enabled = false

    const ax = avatarEntry.group.position.x
    const ay = avatarEntry.group.position.y
    const az = avatarEntry.group.position.z
    const targetYaw = avatarEntry.group.rotation.y

    if (effective === 'follow') {
      avatarEntry.group.visible = avatarVisibleUnlessVoidDeath(avatarEntry)
      applyFov(CAMERA_FOV)

      // 1) カメラ方位はプレイヤー操作（ドラッグ）主導にする。snap 時のみアバターの
      //    背後へ初期化し、以降はアバターの向きを自動追従しない。自動追従すると
      //    「カメラ基準で進む」移動と相互に追いかけ合って旋回し続けるため断ち切る。
      //    ドラッグで向けた方向へ avatar が進む操作（カメラ相対移動）を成立させる。
      if (followNeedsSnap) {
        followYaw = targetYaw
        cameraYawOffset = 0
      }
      // 乗車中はカメラ方位を車の背後へ自動追従させる（Roblox の VehicleCamera
      // 相当。旋回するとカメラが車の後ろへ回り込む）。ドラッグの
      // cameraYawOffset はそのまま効くので、運転中の見回しも保てる。
      if (cameraSeated && !overviewMode) {
        const yawDelta = Math.atan2(
          Math.sin(targetYaw - followYaw),
          Math.cos(targetYaw - followYaw),
        )
        followYaw += yawDelta * Math.min(1, dt * VEHICLE_CAMERA_YAW_FOLLOW)
      }
      // 方向キー左右での見回し。ドラッグと同じ向き（→ で右へ視点が回る）へ、
      // 押している間だけ一定角速度で回す。乗車中は ←→ がハンドル操作
      // （vehicleSystem の操舵）のため、カメラは回さない。
      if (!cameraSeated) {
        const rotateInput =
          (cameraRotateHeld.has('ArrowLeft') ? 1 : 0) -
          (cameraRotateHeld.has('ArrowRight') ? 1 : 0)
        if (rotateInput !== 0) {
          const yawStep = rotateInput * ARROW_CAMERA_YAW_SPEED * dt
          cameraYawOffset += yawStep
          // 実カメラオフセット（followCamOffset）も同フレームで同角度回す。
          // 目標角度だけ進めると位置 lerp（FOLLOW_POS_SMOOTHING）が追いつく
          // まで回転が残り、キーを離した後も減速しながら回って見えるため。
          if (followCamOffset !== null) {
            const cos = Math.cos(yawStep)
            const sin = Math.sin(yawStep)
            const ox = followCamOffset.x
            const oz = followCamOffset.z
            followCamOffset.x = ox * cos + oz * sin
            followCamOffset.z = oz * cos - ox * sin
          }
        }
      }

      // 2) followYaw + ユーザードラッグオフセットを合算した yaw/pitch から、
      //    アバターを中心とした spherical 座標でカメラ位置を計算する。
      //    three.js 標準: rotation.y = θ のとき local -Z の世界向きは (-sin(θ), 0, -cos(θ))。
      // followViewYawOffset は dev 専用（motion-tuner の側面ビュー）。prod では
      // import.meta.env.DEV が false 定数になり、この加算は 0 に畳まれて消える。
      const yaw =
        followYaw +
        cameraYawOffset +
        (import.meta.env.DEV ? followViewYawOffset : 0)
      // 俯瞰（停止中）はユーザーのピッチ/ズーム操作より優先し、毎回同じ
      // 「上から見下ろす遠目」へ揃える。通常時は従来どおりユーザー操作主導。
      const pitch =
        overviewMode ? OVERVIEW_PITCH : (
          Math.min(
            FOLLOW_PITCH_MAX,
            Math.max(FOLLOW_PITCH_MIN, FOLLOW_BASE_PITCH + cameraPitchOffset),
          )
        )
      const distanceScale =
        overviewMode ?
          FOLLOW_DISTANCE_DEFAULT * OVERVIEW_DISTANCE_FACTOR
        : followDistance
      // 乗車中は車体が画角へ収まるよう引きで追う（乗降時は滑らかに遷移）。
      seatedZoomAmp +=
        ((cameraSeated && !overviewMode ? 1 : 0) - seatedZoomAmp) *
        Math.min(1, dt * VEHICLE_CAMERA_ZOOM_LERP)
      const baseDist =
        Math.hypot(5, 2.5) *
        scaleH *
        distanceScale *
        (1 + (VEHICLE_CAMERA_DISTANCE_FACTOR - 1) * seatedZoomAmp)
      const horizDist = Math.cos(pitch) * baseDist
      const vertDist = Math.sin(pitch) * baseDist
      const fx = -Math.sin(yaw)
      const fz = -Math.cos(yaw)
      const targetOffsetX = -fx * horizDist
      const targetOffsetY = vertDist
      const targetOffsetZ = -fz * horizDist

      // 3) アバター→カメラのオフセットを lerp（初回・モード遷移直後はスナップ）。
      //    アバターの移動そのもの（歩行・ジャンプ・落下）には剛体追従させ、
      //    落下中にカメラだけ上へ取り残されて視線が下へ倒れ込む（=前に傾いて
      //    見えて酔う）のを防ぐ。ドラッグ・ズーム・俯瞰切替は滑らかに遷移する。
      if (followCamOffset === null || followNeedsSnap) {
        if (followCamOffset === null) {
          followCamOffset = new THREE.Vector3(
            targetOffsetX,
            targetOffsetY,
            targetOffsetZ,
          )
        } else {
          followCamOffset.set(targetOffsetX, targetOffsetY, targetOffsetZ)
        }
      } else {
        const posAlpha = Math.min(1, Math.max(0, dt * FOLLOW_POS_SMOOTHING))
        followOffsetTarget.set(targetOffsetX, targetOffsetY, targetOffsetZ)
        followCamOffset.lerp(followOffsetTarget, posAlpha)
      }
      // スナップは 1 フレームのみ。
      followNeedsSnap = false

      // 4) カメラへ反映し、注視点はアバター頭付近（胸上）— ここは簡易にアバター位置直結。
      camera.position.set(
        ax + followCamOffset.x,
        ay + followCamOffset.y,
        az + followCamOffset.z,
      )
      camera.lookAt(ax, ay + 1.5 * scaleH, az)
      return
    }

    // first: 頭位置に置き、forward を見る。アバター自身は非表示。FOV は firstFov。
    // 一人称は厳密な方向追従が望ましいため smoothing は適用しない。
    applyFov(firstFov)
    // three.js 標準 forward = (-sin(θ), 0, -cos(θ))。
    const fx = -Math.sin(targetYaw)
    const fz = -Math.cos(targetYaw)
    const headY = ay + 2 * scaleH
    camera.position.set(ax, headY, az)
    camera.lookAt(ax + fx, headY, az + fz)
    avatarEntry.group.visible = false
  }

  /**
   * 外部 API / ホットキーから視点モードを設定する。
   * 非 free → free 復帰時は OrbitControls.target をアバター位置にスナップし、
   * カメラジャンプを避ける。
   */
  const setCameraMode = (mode: CameraMode): void => {
    if (cameraMode === mode) {
      return
    }
    const previous = cameraMode
    cameraMode = mode
    // follow を離れる瞬間に進行中ドラッグを打ち切る。pointermove を挟まずに
    // follow → 他 → follow と戻ると古いドラッグ座標で視点が飛ぶのを防ぐ。
    if (previous === 'follow' && mode !== 'follow') {
      cancelFollowDrag()
    }
    if (previous !== 'free' && mode === 'free') {
      // free 復帰: OrbitControls の target をアバター位置に合わせて更新し、
      // すこし遠目の位置からの見下ろし視点に置き直す（アバター視点より引いた画）。
      if (avatarEntry) {
        const scaleH = avatarEntry.scaleHKey ?? 0
        controls.target.set(
          avatarEntry.group.position.x,
          avatarEntry.group.position.y + 1.5 * scaleH,
          avatarEntry.group.position.z,
        )
        const freeDist =
          Math.hypot(5, 2.5) * scaleH * FREE_ENTRY_DISTANCE_FACTOR
        if (freeDist > 1e-3) {
          // 現在の視線方向を保ったまま注視点から距離だけ引く。
          // 真上などで方向が定まらない場合は斜め後ろ上をフォールバックにする。
          const dir = camera.position.clone().sub(controls.target)
          if (dir.lengthSq() < 1e-6) {
            dir.set(0, 2.5, 5)
          }
          dir.normalize()
          camera.position.copy(controls.target).addScaledVector(dir, freeDist)
        }
      }
      controls.enabled = true
      controls.update()
    } else if (previous === 'free' && mode !== 'free') {
      // free → 非 free: OrbitControls を無効化（updateCamera 内でも保証される）。
      controls.enabled = false
    }
    // follow へ遷移したら次フレームでヨー・カメラ位置をアバターへスナップする
    // （非 follow 中に蓄積した古い followYaw/followCamOffset からの大ジャンプを防ぐ）。
    // あわせて距離・ドラッグ量を初期値へ戻し、アバター視点を既定の見え方に揃える。
    if (mode === 'follow') {
      followNeedsSnap = true
      followDistance = FOLLOW_DISTANCE_DEFAULT
      cameraYawOffset = 0
      cameraPitchOffset = 0
    }
    // アバターを非表示にしていた場合の即時復帰（first → 他へ抜けるケース）。
    // 死亡演出（void）で消している間は復帰させない。
    if (avatarEntry && mode !== 'first') {
      avatarEntry.group.visible = avatarVisibleUnlessVoidDeath(avatarEntry)
    }
    // ボタン経由・KeyV ホットキーのどちらでも外側 UI へモード変化を通知する。
    options?.onCameraModeChange?.(mode)
  }

  // --- 視点切替ホットキー（KeyV） ------------------------------------------

  const CAMERA_MODE_CYCLE: readonly CameraMode[] = ['free', 'follow', 'first']

  const handleKeyDown = (event: Event): void => {
    if (!isKeyboardEventLike(event)) {
      return
    }
    if (event.code !== 'KeyV') {
      return
    }
    if (event.repeat) {
      return
    }
    if (isTextEntryTarget(event.target)) {
      return
    }
    const index = CAMERA_MODE_CYCLE.indexOf(cameraMode)
    const next =
      CAMERA_MODE_CYCLE[(index + 1) % CAMERA_MODE_CYCLE.length] ?? 'free'
    event.preventDefault()
    setCameraMode(next)
  }

  const keyboardTarget: Window | HTMLElement = options?.keyboardTarget ?? window
  // 視点はアバター（follow）のみが製品仕様。切替ホットキーは devtools 限定。
  const cameraHotkeyEnabled = options?.cameraHotkeyEnabled === true
  if (cameraHotkeyEnabled) {
    keyboardTarget.addEventListener('keydown', handleKeyDown)
  }

  // --- 方向キー左右での視点回転（Roblox 本家の既定操作） -------------------
  //
  // ←→ はキャラの横移動ではなくカメラの見回し（横移動は A/D）。押下状態だけ
  // ここで記録し、回転は follow カメラの毎フレーム更新で dt に比例して行う
  // （keydown のリピート間隔に依存させない）。preventDefault は物理側の
  // ARROW_KEYS 処理が担うため、ここでは状態記録のみ行う。
  const cameraRotateHeld = new Set<string>()
  const handleCameraRotateKeyDown = (event: Event): void => {
    if (!isKeyboardEventLike(event)) {
      return
    }
    if (event.code !== 'ArrowLeft' && event.code !== 'ArrowRight') {
      return
    }
    if (isTextEntryTarget(event.target)) {
      return
    }
    cameraRotateHeld.add(event.code)
  }
  const handleCameraRotateKeyUp = (event: Event): void => {
    if (!isKeyboardEventLike(event)) {
      return
    }
    cameraRotateHeld.delete(event.code)
  }
  // フォーカス喪失中の keyup は届かないため、blur で押下状態を必ず捨てる。
  // keyboardTarget（コンテナ）の blur も購読するのは、キーを押したまま
  // Blockly 等コンテナ外をクリックすると keyup がコンテナへ届かず、視点が
  // 回り続けるため（physicsController の releasePhysicalKeys と同じ対称性）。
  const handleCameraRotateBlur = (): void => {
    cameraRotateHeld.clear()
  }
  keyboardTarget.addEventListener('keydown', handleCameraRotateKeyDown)
  keyboardTarget.addEventListener('keyup', handleCameraRotateKeyUp)
  keyboardTarget.addEventListener('blur', handleCameraRotateBlur)
  // window blur の購読は、PIP で keyboardTarget が別ウインドウへ移動しても
  // 追従できるよう、末尾の startKeyboardTargetLifecycle でまとめて行う。

  // --- マウスホイールでのズーム（follow=距離 / first=FOV） ---------------
  //
  // free モードは OrbitControls の dolly に委ねるため preventDefault しない。
  // wheel リスナは canvas に貼り、ビューポート外のスクロールは妨げない。

  const clamp = (value: number, min: number, max: number): number =>
    Math.min(max, Math.max(min, value))

  const handleWheel = (event: WheelEvent): void => {
    const effective = getEffectiveCameraMode()
    if (effective === 'free') {
      // OrbitControls がページスクロールを抑制した上で dolly する。委譲。
      return
    }
    if (effective === 'follow') {
      event.preventDefault()
      // 俯瞰（停止中）は距離固定。見た目が変わらないのに followDistance だけ
      // 溜まり、再生した瞬間にズームが突然反映される「不可視な状態変更」を防ぐ。
      if (overviewMode) {
        return
      }
      // 上スクロール(deltaY<0)=ズームイン=距離減少。
      followDistance = clamp(
        followDistance + event.deltaY * FOLLOW_WHEEL_STEP,
        FOLLOW_DISTANCE_MIN,
        FOLLOW_DISTANCE_MAX,
      )
      return
    }
    // first: FOV を狭める/広げる。
    event.preventDefault()
    firstFov = clamp(
      firstFov + event.deltaY * FIRST_FOV_WHEEL_STEP,
      FIRST_FOV_MIN,
      FIRST_FOV_MAX,
    )
  }

  // canvas に bind することで、UI 上の他要素のスクロールへ影響を与えない。
  // passive:false にしないと preventDefault が無効化される。
  canvas.addEventListener('wheel', handleWheel, { passive: false })

  // follow モードでマウスドラッグによる視点回転を可能にする。
  // free モードは OrbitControls 側で、first モードは方向追従でカメラを決めるため
  // どちらもここでは扱わない（カメラオフセットを使うのは follow のみ）。
  // pointerId と最後の座標を保持して、ドラッグ中のみカメラオフセットを更新する。
  let dragPointerId: number | null = null
  let lastDragX = 0
  let lastDragY = 0
  const handleFollowPointerDown = (event: PointerEvent): void => {
    if (getEffectiveCameraMode() !== 'follow') return
    // デスクトップは右ボタンドラッグで視点回転（Roblox 本家準拠、contextmenu は
    // 抑制済み）。タブレット等のタッチ/ペンは 1 本指ドラッグで回転させる
    // （タッチの pointerdown は button=0 で報告され、右ボタン条件では拾えない）。
    // canvas に届くのは KeyGuide ボタン等のオーバーレイ外の素のドラッグだけ。
    const isTouch = event.pointerType === 'touch' || event.pointerType === 'pen'
    if (!isTouch && event.button !== 2) return
    dragPointerId = event.pointerId
    lastDragX = event.clientX
    lastDragY = event.clientY
    canvas.setPointerCapture(event.pointerId)
  }
  const cancelFollowDrag = (): void => {
    if (dragPointerId === null) return
    try {
      canvas.releasePointerCapture(dragPointerId)
    } catch {
      // already released
    }
    dragPointerId = null
  }
  const handleFollowPointerMove = (event: PointerEvent): void => {
    if (dragPointerId !== event.pointerId) return
    // モード切替を跨いだドラッグは打ち切る。follow 以外ではオフセットを使わず、
    // かつ古い lastDragX/Y のまま follow へ戻ると次の move で視点が飛ぶため。
    if (getEffectiveCameraMode() !== 'follow') {
      cancelFollowDrag()
      return
    }
    const dx = event.clientX - lastDragX
    const dy = event.clientY - lastDragY
    lastDragX = event.clientX
    lastDragY = event.clientY
    // 水平: yaw（右ドラッグでアバターの右側に回り込む）。
    // 垂直: pitch。Roblox 本家に合わせ、下ドラッグでカメラが上がり=見下ろす、
    // 上ドラッグで見上げる。
    cameraYawOffset -= dx * FOLLOW_DRAG_YAW_SENSITIVITY
    // 俯瞰（停止中）はピッチ固定。ヨー回転（周囲の見回し）だけ許可し、見た目に
    // 反映されないピッチオフセットが溜まって再生時に突然跳ねるのを防ぐ。
    if (overviewMode) {
      return
    }
    // ピッチオフセットは実ピッチが [MIN, MAX] に収まる範囲へ即クランプする。
    // 合算後だけをクランプすると、上下に振り切ったぶんオフセットが可動域外へ
    // 際限なく溜まり、戻し操作のたびにその「借金」を解消するまでカメラが
    // 動かなくなる（＝見上げたまま元に戻せない）。オフセット自体を束縛して防ぐ。
    cameraPitchOffset = clamp(
      cameraPitchOffset + dy * FOLLOW_DRAG_PITCH_SENSITIVITY,
      FOLLOW_PITCH_MIN - FOLLOW_BASE_PITCH,
      FOLLOW_PITCH_MAX - FOLLOW_BASE_PITCH,
    )
  }
  const endFollowDrag = (event: PointerEvent): void => {
    if (dragPointerId !== event.pointerId) return
    try {
      canvas.releasePointerCapture(event.pointerId)
    } catch {
      // already released
    }
    dragPointerId = null
  }
  // タッチの 1 本指ドラッグで視点回転を成立させる。touch-action 既定のままだと
  // ブラウザがスクロール/ピンチズームへジェスチャを横取りし pointermove が
  // 届かない。none にして canvas 上のドラッグをすべてポインタイベントへ回す。
  // OrbitControls も構築時に同値を設定するが、follow ドラッグの成立を OrbitControls
  // のライフサイクルに依存させないため明示的に設定する。
  canvas.style.touchAction = 'none'
  canvas.addEventListener('pointerdown', handleFollowPointerDown)
  canvas.addEventListener('pointermove', handleFollowPointerMove)
  canvas.addEventListener('pointerup', endFollowDrag)
  canvas.addEventListener('pointercancel', endFollowDrag)

  // マウスホバーでクリック可能オブジェクト上にカーソルがあるとき pointer 表示にする。
  // クリック raycaster とは別系統（setFromCamera の競合回避）。タッチ端末は対象外。
  const hoverRaycaster = new THREE.Raycaster()
  const hoverPointer = new THREE.Vector2()
  let lastHoverRaycastTime = 0
  let lastHoverCursor = ''
  const resetHoverCursor = (): void => {
    if (lastHoverCursor !== '') {
      lastHoverCursor = ''
      canvas.style.cursor = ''
    }
  }
  const handleHoverMove = (event: PointerEvent): void => {
    if (enableBuilder || !options?.onObjectClick) {
      return
    }
    if (event.pointerType !== 'mouse') {
      return
    }
    // follow の視点回転ドラッグ中はホバー判定をスキップ（free の OrbitControls
    // 回転中は dragPointerId が立たないため throttle 任せ）。
    if (dragPointerId !== null) {
      return
    }
    const now = performance.now()
    if (now - lastHoverRaycastTime < HOVER_RAYCAST_THROTTLE_MS) {
      return
    }
    lastHoverRaycastTime = now
    const rect = renderer.domElement.getBoundingClientRect()
    hoverPointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    hoverPointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
    hoverRaycaster.setFromCamera(hoverPointer, camera)
    const objectId = firstClickableHit(
      hoverRaycaster.intersectObjects(collectClickableObjects(), true),
    )
    const nextCursor = objectId === undefined ? '' : 'pointer'
    if (nextCursor !== lastHoverCursor) {
      lastHoverCursor = nextCursor
      canvas.style.cursor = nextCursor
    }
  }
  canvas.addEventListener('pointermove', handleHoverMove)
  canvas.addEventListener('pointerleave', resetHoverCursor)
  // window blur の購読は、PIP で keyboardTarget が別ウインドウへ移動しても
  // 追従できるよう、末尾の startKeyboardTargetLifecycle でまとめて行う。

  // PIP（documentPictureInPicture）で keyboardTarget（container）が別ウインドウの
  // document へ移動しても、この 3 つの window blur 購読（ポインタドラッグ解除・
  // カメラ回転状態クリア・ホバーカーソル解除）が追従するよう、固定の window では
  // なく「今属している window」へまとめて張り直す。フォーカスを得るたびに
  // 再バインドする（PIP 切替時は focusViewport() が container.focus() を呼ぶため、
  // そのタイミングで自然に追従する）。startKeyboardTargetLifecycle（後述）に渡す。
  const windowBlurHandlers = [
    handleWindowBlur,
    handleCameraRotateBlur,
    resetHoverCursor,
  ]

  // 描画ループ。
  const renderLoop = (now: number): void => {
    // 経過時間（秒）。バックグラウンド復帰時の位相ジャンプを 0.1s で頭打ち。
    const dt =
      lastAnimateTime === null ? 0 : (
        Math.min(0.1, Math.max(0, (now - lastAnimateTime) / 1000))
      )
    lastAnimateTime = now
    // アバターアニメーション（移動検知 + 四肢振り + ジャンプ屈伸）。
    if (avatarEntry) {
      const motionState =
        avatarMotionStateProvider ? avatarMotionStateProvider(now) : null
      cameraSeated = motionState?.isSeated ?? false
      // リスポーン/リセット（respawnGeneration の増加）で視点角度を背後初期へ
      // 戻す。同フレーム後段の updateCamera(dt) で followYaw=アバター向き・
      // オフセット 0 にスナップされる。
      if (
        motionState &&
        motionState.respawnGeneration !== lastRespawnGeneration
      ) {
        lastRespawnGeneration = motionState.respawnGeneration
        followNeedsSnap = true
        cameraYawOffset = 0
        cameraPitchOffset = 0
      }
      // 表示位置は物理側の補間済み位置（renderPosition）を優先する。物理の
      // 固定ステップと rAF の周期ずれによる歩行のガタつきを抑えるため。
      // World の位置（判定・スクリプトの真実源）は物理確定位置のまま。
      if (motionState?.renderPosition) {
        avatarEntry.group.position.set(
          motionState.renderPosition.x,
          motionState.renderPosition.y,
          motionState.renderPosition.z,
        )
      }
      animateVrmAvatar(avatarEntry, dt, motionState)
    }
    // 炎エフェクトのパーティクルを進める（fire=true の entry のみ生成されている）。
    for (const entry of entries.values()) {
      entry.fireEffect?.update(dt)
      entry.sparkleEffect?.update(dt)
    }
    // 水面の波アニメーション。
    terrainView?.update(dt)
    // カメラ位置/lookAt をモード別に確定（follow は dt で平滑化）。
    updateCamera(dt)
    // 夜空はカメラへ追従させ、無限遠の背景として常に同じ見え方にする。
    if (nightSky) {
      nightSky.group.position.copy(camera.position)
    }
    if (getEffectiveCameraMode() === 'free') {
      controls.update()
    }
    // 巨大シーンのチャンク距離カリング（毎フレームは不要なので間引く）。
    if (staticBoxBatcher) {
      cullFrame += 1
      if (cullFrame % CULL_CHECK_INTERVAL === 0) {
        // 注視点はモード別に取る。free はズームで注視点から離れるため controls.target、
        // follow/first はアバター（近接）を注視点にして focalDistance を小さく保ち、
        // 距離カリングの最適化を効かせる（controls.target は free でしか更新されない）。
        const focalDistance =
          getEffectiveCameraMode() !== 'free' && avatarEntry ?
            camera.position.distanceTo(avatarEntry.group.position)
          : camera.position.distanceTo(controls.target)
        staticBoxBatcher.updateCulling(camera.position, focalDistance)
      }
    }
    // 視点とアバターの間の遮蔽物を半透明化する（毎フレームは重いので間引く）。
    occlusionFrame += 1
    if (occlusionFrame % OCCLUSION_CHECK_INTERVAL === 0) {
      updateOcclusionFade()
    }
    renderer.render(scene, camera)
  }
  // rAF ループと 3 つの window blur 購読を、PIP で keyboardTarget が別ウインドウ
  // へ移動しても追従する形でまとめて管理する（フォーカスを得た瞬間に、待機中の
  // rAF を旧 window でキャンセルして新しい window へ即座に付け替える。renderLoop
  // 内で「次フレーム到達時」に再評価するだけだと、旧 window が既にバックグラウ
  // ンド化していて次フレームが来ないケースを取りこぼすため）。
  const keyboardTargetLifecycle = startKeyboardTargetLifecycle(
    keyboardTarget,
    renderLoop,
    windowBlurHandlers,
  )

  // three / DOM リソースの解放本体（必ず最後まで走るべき処理群）。
  const disposeAll = (): void => {
    if (transformControls) {
      transformControls.detach()
      transformControls.dispose()
      transformControls = null
    }
    if (gizmoHelper) {
      scene.remove(gizmoHelper)
      gizmoHelper = null
    }

    for (const entry of entries.values()) {
      scene.remove(entry.container)
      disposeMeshEntry(entry)
    }
    entries.clear()
    occludedIds.clear()

    // 静的 box バッチャ（InstancedMesh + バケットマテリアル）と共有スタッド・
    // バケット模様テクスチャを解放。
    staticBoxBatcher?.dispose()
    sharedStudTexture.dispose()
    for (const texture of bucketPatternTextures) {
      texture.dispose()
    }
    bucketPatternTextures.length = 0
    // URL 単位で共有しているメッシュ／decal テクスチャを一括解放する。各 entry の
    // disposeMeshEntry は shared フラグでこれらの個別解放をスキップしている。
    meshTextureCache.dispose()
    decalTextureCache.dispose()

    // アバターを解放（state にアバターが含まれない場合は no-op）。
    removeAvatar()
    lastAnimateTime = null

    if (grid) {
      grid.geometry.dispose()
      const gridMaterial = grid.material
      if (Array.isArray(gridMaterial)) {
        for (const material of gridMaterial) {
          material.dispose()
        }
      } else {
        gridMaterial.dispose()
      }
      scene.remove(grid)
    }
    if (terrainView) {
      scene.remove(terrainView.group)
      terrainView.dispose()
    }
    if (nightSky) {
      scene.remove(nightSky.group)
      nightSky.dispose()
    }
    if (staticMeshView) {
      scene.remove(staticMeshView.group)
      staticMeshView.dispose()
    }
    scene.remove(ambient)
    scene.remove(directional)
    scene.remove(hemisphere)

    controls.dispose()
    renderer.dispose()

    if (canvas.parentNode === container) {
      container.removeChild(canvas)
    }
  }

  let disposed = false
  const dispose = (): void => {
    if (disposed) {
      return
    }
    disposed = true

    keyboardTargetLifecycle.dispose()
    // 外部実装（PreviewWorld）の購読解除が例外を投げても、
    // three/DOM リソースの解放は必ず最後まで走らせる。
    try {
      unsubscribe()
    } finally {
      // first モードでアバターを非表示にしていた場合に備えて表示復帰してから解放。
      if (avatarEntry) {
        avatarEntry.group.visible = true
      }
      resizeObserver.disconnect()
      canvas.removeEventListener('pointerdown', handlePointerDownCapture, true)
      canvas.removeEventListener('pointerdown', handlePointerDown)
      canvas.removeEventListener('pointerup', handlePointerUp)
      canvas.removeEventListener('pointercancel', handlePointerCancel)
      canvas.removeEventListener('wheel', handleWheel)
      canvas.removeEventListener('pointerdown', handleFollowPointerDown)
      canvas.removeEventListener('pointermove', handleFollowPointerMove)
      canvas.removeEventListener('pointerup', endFollowDrag)
      canvas.removeEventListener('pointercancel', endFollowDrag)
      canvas.removeEventListener('pointermove', handleHoverMove)
      canvas.removeEventListener('pointerleave', resetHoverCursor)
      if (cameraHotkeyEnabled) {
        keyboardTarget.removeEventListener('keydown', handleKeyDown)
      }
      keyboardTarget.removeEventListener('keydown', handleCameraRotateKeyDown)
      keyboardTarget.removeEventListener('keyup', handleCameraRotateKeyUp)
      keyboardTarget.removeEventListener('blur', handleCameraRotateBlur)
      disposeAll()
    }
  }

  const setAvatarMotionStateProvider = (
    provider: ((nowMs: number) => AvatarMotionState) | null,
  ): void => {
    avatarMotionStateProvider = provider
  }

  /**
   * アバターのモーション内部状態（走り位相・走行/ダッシュエンベロープ・前傾・
   * バウンス・直近位置）を初期化する。Preview の reset でアバターが spawn へ
   * テレポートする際に呼び、残留した前傾/増幅やテレポート由来の距離スパイクを防ぐ。
   */
  const resetAvatarMotion = (): void => {
    if (!avatarEntry) return
    avatarEntry.runTime = 0
    avatarEntry.strideAmplitude = 0
    avatarEntry.dashAmplitude = 0
    avatarEntry.bobOffset = 0
    // 遊泳ポーズも即時解除（水中で停止→再生したとき spawn 上で前傾・Z 補正が
    // 数フレーム残らないようにする）。
    avatarEntry.swimPoseAmp = 0
    avatarEntry.swimMoveAmp = 0
    avatarEntry.slidePoseAmp = 0
    // 着席ポーズも即時解除（乗車中に停止→再生したとき spawn 上で座位が
    // 数フレーム残らないようにする）。
    avatarEntry.sitPoseAmp = 0
    avatarEntry.wasSwimmingMoving = false
    avatarEntry.swimTime = 0
    // 次フレームの距離差分が暴れないよう prev を spawn に合わせる。
    const spawn = descriptor.avatar?.spawn
    if (spawn) {
      avatarEntry.prevX = spawn.x
      avatarEntry.prevY = spawn.y
      avatarEntry.prevZ = spawn.z
    }
    avatarEntry.modelRoot.rotation.x = 0
    avatarEntry.modelRoot.position.set(0, avatarEntry.modelRestY, 0)
    avatarEntry.placeholderMesh.position.y = 0
  }

  /**
   * 移動入力をカメラ基準へ変換するための現在カメラ方位（ラジアン）。follow（アバター
   * 視点）のときだけ返し、それ以外（free/first）は null（＝ワールド基準移動のまま）。
   * 物理側がこの方位で WASD/矢印入力を回転し、視点を向けた方向へ進めるようにする。
   */
  const getMovementBasisYaw = (): number | null => {
    if (getEffectiveCameraMode() !== 'follow' || !avatarEntry) {
      return null
    }
    // snap 保留中（follow 遷移直後）は次フレームで followYaw=アバター向き・offset=0 に
    // なるため、その確定値（アバターの向き）を先取りして返す。描画更新前に物理が
    // 参照しても古い followYaw+offset を渡さない。
    return followNeedsSnap ?
        avatarEntry.group.rotation.y
      : followYaw + cameraYawOffset
  }

  /** 停止中の俯瞰ビュー切替（PreviewPanel の再生/停止ハンドラから呼ぶ）。 */
  const setCameraOverview = (
    enabled: boolean,
    options?: { snapToAvatar?: boolean },
  ): void => {
    overviewMode = enabled
    // 再生（spawn リセット）時は古い停止地点から lerp で追いかけず、
    // 次フレームでアバター背後（向き・位置とも）へ即時スナップする。
    if (options?.snapToAvatar) {
      followNeedsSnap = true
    }
  }

  /**
   * dev 専用: follow カメラの見る角度の固定オフセット（ラジアン）を設定する。
   * 進行方向（movementBasisYaw）には影響しないため、アバターを真横〜斜め後方
   * から眺めつつ、これまで通りの方向へ走らせ続けられる。
   */
  const setFollowViewYawOffset = (radians: number): void => {
    followViewYawOffset = radians
  }

  return {
    dispose,
    setSelected,
    setGizmoMode,
    setCameraMode,
    setAvatarMotionStateProvider,
    resetAvatarMotion,
    getMovementBasisYaw,
    setCameraOverview,
    setFollowViewYawOffset,
  }
}
